"use client";

import * as React from "react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { CardPreview } from "@/components/checkout/CardPreview";
import { pushCheckoutPaymentMethodSelected, pushCheckoutPaymentStep } from "@/lib/marketing/data-layer";

type Props = {
  tenantSlug: string;
  defaultType: "A1" | "A3";
  priceCents: number;
  whatsappLink?: string | null;
};

type ProductOption = {
  id: string;
  name: string;
  publicName: string;
  priceCents: number | null;
};

type PaymentMethod = "PIX" | "CREDIT_CARD" | "BOLETO" | "LOJA";
type PaymentResult = {
  kind: "pix" | "boleto" | "loja" | "credito";
  title: string;
  description: string;
  qrCode?: string;
  copyPaste?: string;
  actionUrl?: string;
  actionLabel?: string;
  status?: string;
  id?: string;
  order?: string;
  name?: string;
  category?: string;
  atendimento?: string;
  protocolo?: string;
  price?: number;
  appointmentLabel?: string;
  biometryMessage?: string;
};

function formatBRLFromCents(cents: number) {
  return (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function onlyDigits(v: string) {
  return v.replace(/\D/g, "");
}

function maskCpfCnpj(v: string) {
  const d = onlyDigits(v).slice(0, 14);
  if (d.length <= 11) {
    // CPF: 000.000.000-00
    return d
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  // CNPJ: 00.000.000/0000-00
  return d
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
}

function maskPhone(v: string) {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
}

function maskCep(v: string) {
  const d = onlyDigits(v).slice(0, 8);
  return d.replace(/(\d{5})(\d)/, "$1-$2");
}

function maskCardNumber(v: string) {
  const d = onlyDigits(v).slice(0, 19);
  return d.replace(/(\d{4})(?=\d)/g, "$1 ");
}

function maskExpiry(v: string) {
  const d = onlyDigits(v).slice(0, 4);
  return d.replace(/(\d{2})(\d)/, "$1/$2");
}

function maskBirthDate(v: string) {
  const d = onlyDigits(v).slice(0, 8);
  return d.replace(/(\d{2})(\d)/, "$1/$2").replace(/(\d{2})\/(\d{2})(\d)/, "$1/$2/$3");
}

function isValidBrDate(value: string) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!m) return false;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) return false;
  if (year < 1900 || year > 2100) return false;
  if (month < 1 || month > 12) return false;
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

function isValidCardExpiry(value: string) {
  const m = /^(\d{2})\/(\d{2})$/.exec(value.trim());
  if (!m) return false;
  const month = Number(m[1]);
  const year = Number(`20${m[2]}`);
  if (month < 1 || month > 12) return false;
  const now = new Date();
  const expiry = new Date(year, month, 0, 23, 59, 59, 999);
  return expiry.getTime() >= now.getTime();
}

function sanitizeName(v: string) {
  return v.replace(/[^a-zA-ZÀ-ÖØ-öø-ÿ\s'’.-]/g, "");
}

function formatCelularCobranca(maskedPhone: string) {
  const d = onlyDigits(maskedPhone);
  if (d.length < 10) return maskedPhone;
  const ddd = d.slice(0, 2);
  const num = d.slice(2);
  if (num.length === 9) return `+55 ${ddd} ${num.slice(0, 5)}-${num.slice(5)}`;
  return `+55 ${ddd} ${num.slice(0, 4)}-${num.slice(4)}`;
}

const DOW = ["D", "S", "T", "Q", "Q", "S", "S"] as const;
const MONTHS = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro"
] as const;

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function toYmd(year: number, monthIndex0: number, day: number) {
  return `${year}${pad2(monthIndex0 + 1)}${pad2(day)}`;
}

function daysInMonth(year: number, monthIndex0: number) {
  return new Date(year, monthIndex0 + 1, 0).getDate();
}

function firstDow(year: number, monthIndex0: number) {
  return new Date(year, monthIndex0, 1).getDay(); // 0..6
}

function parseAvailableOptions(data: any): Array<{ id: number; label: string }> {
  const opts: Array<{ id: number; label: string }> = [];
  const arr = Array.isArray(data) ? data : Array.isArray(data?.hours) ? data.hours : Array.isArray(data?.available_hours) ? data.available_hours : Array.isArray(data?.data) ? data.data : [];
  for (const it of arr) {
    const id = Number(it?.id_agenda ?? it?.calendar_id ?? it?.id);
    if (!Number.isFinite(id)) continue;
    const label = String(it?.hour ?? it?.hora ?? it?.label ?? it?.time ?? `ID ${id}`);
    opts.push({ id, label });
  }
  return opts;
}

function normalizeExternalUrl(value: unknown) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  return raw.replace(/[`"' ]+/g, "").trim();
}

function normalizeText(value: string) {
  return (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function formatProductPublicName(value: string) {
  return String(value ?? "")
    .replace(/\s*\((?:id)\s*\d+\)\s*$/i, "")
    .replace(/\s+/g, " ")
    .trim();
}

function parseMoneyToCents(value: unknown): number | null {
  if (value == null) return null;

  if (typeof value === "number" && Number.isFinite(value)) {
    if (!Number.isInteger(value)) return Math.round(value * 100);
    return value >= 1000 ? Math.round(value) : Math.round(value * 100);
  }

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d+$/.test(raw)) {
    const integer = Number(raw);
    if (!Number.isFinite(integer)) return null;
    return raw.length > 3 ? integer : integer * 100;
  }

  const normalized = raw.replace(/[^\d,.-]/g, "").trim();
  if (!normalized) return null;

  const hasComma = normalized.includes(",");
  const hasDot = normalized.includes(".");
  let decimal = normalized;
  if (hasComma && hasDot) {
    decimal = normalized.replace(/\./g, "").replace(",", ".");
  } else if (hasComma) {
    decimal = normalized.replace(",", ".");
  }

  const parsed = Number(decimal);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.round(parsed * 100);
}

function deepFindFirstByKeys(value: any, keys: string[]): unknown {
  const normalizedKeys = new Set(keys.map((key) => normalizeText(key)));

  function walk(input: any, visited = new WeakSet<object>()): unknown {
    if (input == null) return null;
    if (typeof input !== "object") return null;
    if (visited.has(input)) return null;
    visited.add(input);

    if (Array.isArray(input)) {
      for (const item of input) {
        const found = walk(item, visited);
        if (found != null) return found;
      }
      return null;
    }

    for (const [key, nested] of Object.entries(input)) {
      if (normalizedKeys.has(normalizeText(key)) && nested != null && nested !== "") return nested;
    }

    for (const nested of Object.values(input)) {
      const found = walk(nested, visited);
      if (found != null) return found;
    }

    return null;
  }

  return walk(value);
}

function extractProductPriceCents(product: any) {
  const found = deepFindFirstByKeys(product, [
    "price",
    "preco",
    "valor",
    "value",
    "amount",
    "valor_total",
    "preco_total",
    "valor_produto",
    "preco_produto"
  ]);
  return parseMoneyToCents(found);
}

function inferSelectionFromProductName(productName: string, fallbackType: "A1" | "A3") {
  const text = normalizeText(productName);
  if (!text) return null;

  const kind = text.includes("cnpj") ? "e-CNPJ" : text.includes("cpf") ? "e-CPF" : text.includes("mei") ? "e-MEI" : text.includes("nf e") || text.includes("nfe") ? "NF-e" : null;
  if (!kind) return null;

  const validity = text.includes("24") ? "24 meses" : text.includes("3") ? "3 meses" : text.includes("12") ? "12 meses" : null;
  if (!validity) return null;

  const model = text.includes("a1")
    ? "A1"
    : text.includes("cartao")
      ? "A3 Cartão"
      : text.includes("token") || text.includes("kit leitor") || text.includes("kit leitora")
        ? "A3 Token"
        : text.includes("nuvem")
          ? "A3 Nuvem"
          : text.includes("s midia") || text.includes("sem midia")
            ? "A3 s/ Mídia"
            : fallbackType === "A1"
              ? "A1"
              : "A3 Token";

  return {
    kind,
    model,
    validity,
    type: model === "A1" ? "A1" : "A3"
  };
}

function extractOrderIdFromResponse(value: any): string | null {
  if (value == null) return null;
  if (typeof value === "string" || typeof value === "number") {
    const normalized = String(value).trim();
    return normalized || null;
  }
  if (typeof value !== "object") return null;

  if (Array.isArray(value)) {
    for (const item of value) {
      const nested = extractOrderIdFromResponse(item);
      if (nested) return nested;
    }
    return null;
  }

  const direct =
    value?.order_id ??
    value?.id ??
    value?.orderId ??
    value?.orderID ??
    value?.pedido_id ??
    value?.pedidoId ??
    value?.id_pedido ??
    value?.idPedido ??
    value?.codigo_pedido ??
    value?.codigoPedido ??
    value?.numero_pedido ??
    value?.numeroPedido ??
    value?.order_number ??
    value?.orderNumber ??
    value?.numero ??
    value?.codigo;

  if (direct != null && String(direct).trim()) return String(direct).trim();

  for (const key of ["data", "order", "pedido", "result", "response", "retorno", "payload", "content", "raw", "details"]) {
    const nested = extractOrderIdFromResponse(value?.[key]);
    if (nested) return nested;
  }

  for (const nestedValue of Object.values(value)) {
    const nested = extractOrderIdFromResponse(nestedValue);
    if (nested) return nested;
  }

  return null;
}

function getFriendlyCheckoutError(message: string | null | undefined) {
  const text = String(message ?? "").trim();
  if (!text) return "Nao foi possivel concluir esta etapa agora. Tente novamente em instantes.";

  const normalized = normalizeText(text);
  if (
    normalized.startsWith("informe") ||
    normalized.startsWith("complete") ||
    normalized.startsWith("selecione") ||
    normalized.startsWith("preencha") ||
    normalized.includes("revise o campo") ||
    normalized.includes("revise os campos")
  ) {
    return text;
  }

  if (normalized.includes("server action not found") || normalized.includes("rota invalida")) {
    return "Não foi possível conectar à API da SP Certificado Digital (OAuth). Confira no Admin a URL OAuth e a API Base informadas pela SP.";
  }

  if (
    normalized.includes("order id") ||
    normalized.includes("api nao retornou") ||
    normalized.includes("falha ao criar pedido") ||
    normalized.includes("nao foi possivel iniciar o pedido") ||
    normalized.includes("nao foi possivel iniciar seu pedido") ||
    normalized.includes("integracao spcert") ||
    normalized.includes("codigo loja") ||
    normalized.includes("client id") ||
    normalized.includes("client secret")
  ) {
    return "Nao foi possivel iniciar seu pedido agora. Revise os dados e tente novamente em instantes.";
  }

  return text;
}

type CheckoutFieldKey =
  | "product"
  | "name"
  | "email"
  | "phone"
  | "cpf"
  | "cnpj"
  | "birthDate"
  | "fantasyName"
  | "corporateName"
  | "cep"
  | "street"
  | "number"
  | "district"
  | "city"
  | "uf"
  | "billCep"
  | "billStreet"
  | "billNumber"
  | "billDistrict"
  | "billCity"
  | "billUf";

function validateCheckoutStep0(input: {
  spcertEnabled: boolean;
  productId: string | null;
  personType: "PF" | "PJ";
  name: string;
  email: string;
  phone: string;
  cpf: string;
  cnpj: string;
  birthDate: string;
  fantasyName: string;
  corporateName: string;
  billingSame: boolean;
  cep: string;
  street: string;
  number: string;
  district: string;
  city: string;
  uf: string;
  billCep: string;
  billStreet: string;
  billNumber: string;
  billDistrict: string;
  billCity: string;
  billUf: string;
}): { errors: Partial<Record<CheckoutFieldKey, string>>; summary: string | null } {
  const errors: Partial<Record<CheckoutFieldKey, string>> = {};
  const mark = (key: CheckoutFieldKey, message: string) => {
    if (!errors[key]) errors[key] = message;
  };

  if (input.spcertEnabled && !input.productId) {
    mark("product", "Produto ainda não vinculado. Aguarde ou atualize a página.");
  }
  if (!input.name.trim()) mark("name", "Preencha este campo.");
  if (!input.email.includes("@")) mark("email", "Informe um e-mail válido.");
  if (onlyDigits(input.phone).length < 10) mark("phone", "Informe um celular válido com DDD.");

  if (input.personType === "PF") {
    if (onlyDigits(input.cpf).length !== 11) mark("cpf", "Informe um CPF válido.");
    if (!isValidBrDate(input.birthDate)) mark("birthDate", "Use o formato dd/mm/aaaa.");
  } else {
    if (onlyDigits(input.cnpj).length !== 14) mark("cnpj", "Informe um CNPJ válido.");
    if (onlyDigits(input.cpf).length !== 11) mark("cpf", "Informe o CPF do responsável.");
    if (!isValidBrDate(input.birthDate)) mark("birthDate", "Informe a data de nascimento do responsável (dd/mm/aaaa).");
    if (!input.fantasyName.trim()) mark("fantasyName", "Preencha o nome fantasia.");
    if (!input.corporateName.trim()) mark("corporateName", "Preencha a razão social.");
  }

  const billingCep = input.billingSame ? input.cep : input.billCep;
  const billingStreet = input.billingSame ? input.street : input.billStreet;
  const billingNumber = input.billingSame ? input.number : input.billNumber;
  const billingDistrict = input.billingSame ? input.district : input.billDistrict;
  const billingCity = input.billingSame ? input.city : input.billCity;
  const billingUf = input.billingSame ? input.uf : input.billUf;

  const cepKey = input.billingSame ? "cep" : "billCep";
  const streetKey = input.billingSame ? "street" : "billStreet";
  const numberKey = input.billingSame ? "number" : "billNumber";
  const districtKey = input.billingSame ? "district" : "billDistrict";
  const cityKey = input.billingSame ? "city" : "billCity";
  const ufKey = input.billingSame ? "uf" : "billUf";

  if (onlyDigits(billingCep).length !== 8) mark(cepKey, "Informe um CEP válido.");
  if (!billingStreet.trim()) mark(streetKey, "Preencha o endereço.");
  if (!billingNumber.trim()) mark(numberKey, "Informe o número.");
  if (!billingDistrict.trim()) mark(districtKey, "Preencha o bairro.");
  if (!billingCity.trim()) mark(cityKey, "Preencha a cidade.");
  if (billingUf.trim().length !== 2) mark(ufKey, "Use a sigla do estado (ex.: SP).");

  const count = Object.keys(errors).length;
  const summary =
    count === 0
      ? null
      : count === 1
        ? "Revise o campo destacado abaixo."
        : `Revise os ${count} campos destacados abaixo.`;

  return { errors, summary };
}

function scrollToFirstCheckoutFieldError() {
  if (typeof document === "undefined") return;
  const target =
    document.querySelector<HTMLElement>(".inputInvalid") ?? document.querySelector<HTMLElement>(".checkoutProductError");
  target?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function getFieldLabel(field: string) {
  const normalized = normalizeText(field).replace(/\s+/g, "_");
  const labels: Record<string, string> = {
    cpf: "CPF",
    cpf_cobranca: "CPF",
    cnpj: "CNPJ",
    cnpj_cobranca: "CNPJ",
    email: "e-mail",
    email_cobranca: "e-mail",
    celular: "celular",
    celular_cobranca: "celular",
    telefone: "telefone",
    telefone_cobranca: "telefone",
    cep: "CEP",
    cep_cobranca: "CEP",
    logradouro: "endereco",
    logradouro_cobranca: "endereco",
    bairro: "bairro",
    bairro_cobranca: "bairro",
    cidade: "cidade",
    cidade_cobranca: "cidade",
    estado: "estado",
    estado_cobranca: "estado",
    numero: "numero",
    numero_cobranca: "numero",
    nome: "nome",
    nome_completo: "nome",
    nome_completo_cobranca: "nome",
    data_nascimento: "data de nascimento",
    data_nascimento_cobranca: "data de nascimento",
    id_validade: "produto"
  };
  return labels[normalized] ?? field;
}

function extractValidationMessage(payload: any): string | null {
  if (!payload || typeof payload !== "object") return null;

  const candidates = [payload?.errors, payload?.error, payload?.detail, payload?.details, payload?.message];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (typeof candidate === "string") {
      const text = candidate.trim();
      if (text) return getFriendlyCheckoutError(text);
    }

    if (Array.isArray(candidate)) {
      for (const item of candidate) {
        const nested = extractValidationMessage(item);
        if (nested) return nested;
        if (typeof item === "string" && item.trim()) return getFriendlyCheckoutError(item);
      }
    }

    if (typeof candidate === "object") {
      for (const [field, value] of Object.entries(candidate)) {
        if (Array.isArray(value) && value.length > 0) {
          const raw = String(value[0] ?? "").trim();
          if (!raw) continue;
          const normalizedRaw = normalizeText(raw);
          if (
            normalizedRaw.includes("formato") ||
            normalizedRaw.includes("obrigat") ||
            normalizedRaw.includes("invalido") ||
            normalizedRaw.includes("invalida") ||
            normalizedRaw.includes("padrao") ||
            normalizedRaw.includes("deve")
          ) {
            return `${getFieldLabel(field)}: ${raw}`;
          }
          return getFriendlyCheckoutError(raw);
        }
        if (typeof value === "string" && value.trim()) {
          return `${getFieldLabel(field)}: ${value.trim()}`;
        }
        const nested = extractValidationMessage(value);
        if (nested) return nested;
      }
    }
  }

  return null;
}

function parseJsonIfPossible(value: unknown): any {
  if (typeof value !== "string") return value;
  const raw = value.trim();
  if (!raw) return value;
  if (!(raw.startsWith("{") || raw.startsWith("["))) return value;
  try {
    return JSON.parse(raw);
  } catch {
    return value;
  }
}

function deepFindFirstString(value: any, keys: string[]): string {
  const parsed = parseJsonIfPossible(value);
  if (parsed == null) return "";
  if (typeof parsed === "string") return "";
  if (typeof parsed !== "object") return "";

  if (Array.isArray(parsed)) {
    for (const item of parsed) {
      const found = deepFindFirstString(item, keys);
      if (found) return found;
    }
    return "";
  }

  for (const key of keys) {
    const direct = parsed?.[key];
    if (typeof direct === "string" && direct.trim()) return direct.trim();
  }

  for (const nested of Object.values(parsed)) {
    const found = deepFindFirstString(nested, keys);
    if (found) return found;
  }
  return "";
}

function looksLikeQrImage(value: string) {
  const raw = normalizeExternalUrl(value);
  if (!raw) return false;
  return raw.startsWith("data:image/") || /^https?:\/\//i.test(raw);
}

function extractPixPresentation(data: any) {
  const qrCandidates = ["qr_code", "qrCode", "qrcode", "image", "imagem", "qrCodePix", "QrCodePix"];
  const copyCandidates = ["copy_paste", "copyPaste", "copia_cola", "pix", "keyPix", "KeyPix", "pix_code", "pixCode"];

  const qrCodeRaw = deepFindFirstString(data, qrCandidates);
  const copyRaw = deepFindFirstString(data, copyCandidates);

  const qrCode = looksLikeQrImage(qrCodeRaw) ? normalizeExternalUrl(qrCodeRaw) : "";
  const copyPaste = String(copyRaw || "").trim();

  return {
    qrCode: qrCode || undefined,
    copyPaste: copyPaste || undefined
  };
}

function looksLikeGatewayTransactionId(value: unknown) {
  const normalized = String(value ?? "").trim();
  if (!normalized) return false;
  return /^(or|ch|tran)_[A-Za-z0-9]+$/i.test(normalized);
}

function extractDisplayOrderFromResponse(value: any): string | null {
  if (!value || typeof value !== "object") return extractOrderIdFromResponse(value);

  const direct =
    value?.order ??
    value?.pedido ??
    value?.order_number ??
    value?.orderNumber ??
    value?.numero_pedido ??
    value?.numeroPedido ??
    value?.codigo_pedido ??
    value?.codigoPedido ??
    value?.protocolo;

  if (direct != null && String(direct).trim() && !looksLikeGatewayTransactionId(direct)) return String(direct).trim();

  for (const key of ["data", "result", "response", "retorno", "payload", "content", "raw", "details"]) {
    const nested = extractDisplayOrderFromResponse(value?.[key]);
    if (nested) return nested;
  }

  const fallback = extractOrderIdFromResponse(value);
  if (looksLikeGatewayTransactionId(fallback)) return null;
  return fallback;
}

function extractSpcertApiId(value: any): string | null {
  if (value == null) return null;
  if (typeof value !== "object") return null;

  const direct = value?.id ?? value?.ID ?? value?.payment_id ?? value?.paymentId;
  if (direct != null && String(direct).trim() && !looksLikeGatewayTransactionId(direct)) return String(direct).trim();

  for (const key of ["data", "result", "response", "retorno", "payload", "content", "raw", "details"]) {
    const nested = extractSpcertApiId(value?.[key]);
    if (nested) return nested;
  }

  return null;
}

function normalizeBooleanLike(value: unknown): boolean | null {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1 ? true : value === 0 ? false : null;
  if (typeof value !== "string") return null;
  const normalized = normalizeText(value);
  if (!normalized) return null;
  if (["1", "true", "sim", "yes", "ok", "possui", "cadastrada", "disponivel", "disponivel na base", "encontrada"].includes(normalized)) return true;
  if (
    normalized.includes("com biometria") ||
    normalized.includes("possui biometria") ||
    normalized.includes("ja possui biometria") ||
    normalized.includes("biometria cadastrada")
  ) {
    return true;
  }
  if (
    ["0", "false", "nao", "no", "sem", "inexistente"].includes(normalized) ||
    normalized.includes("sem biometria") ||
    normalized.includes("nao possui biometria") ||
    normalized.includes("biometria nao encontrada")
  ) {
    return false;
  }
  return null;
}

function deepFindFirstBooleanLike(value: any, keys: string[]): boolean | null {
  const parsed = parseJsonIfPossible(value);
  if (parsed == null) return null;

  const direct = normalizeBooleanLike(parsed);
  if (direct !== null) return direct;

  if (typeof parsed !== "object") return null;
  if (Array.isArray(parsed)) {
    for (const item of parsed) {
      const found = deepFindFirstBooleanLike(item, keys);
      if (found !== null) return found;
    }
    return null;
  }

  for (const key of keys) {
    const normalized = normalizeBooleanLike(parsed?.[key]);
    if (normalized !== null) return normalized;
  }

  for (const nested of Object.values(parsed)) {
    const found = deepFindFirstBooleanLike(nested, keys);
    if (found !== null) return found;
  }

  return null;
}

function extractBiometryInfo(data: any) {
  const keys = [
    "biometria",
    "biometry",
    "biometric",
    "possui_biometria",
    "possuiBiometria",
    "has_biometry",
    "hasBiometry",
    "has_biometrics",
    "hasBiometrics",
    "biometria_disponivel",
    "biometriaDisponivel",
    "local_biometry",
    "localBiometry"
  ];
  const statusKeys = ["biometria_status", "biometry_status", "status_biometria", "statusBiometria", "message", "mensagem"];
  const hasBiometry = deepFindFirstBooleanLike(data, keys);
  const rawStatus = deepFindFirstString(data, statusKeys);
  const normalizedStatus = normalizeBooleanLike(rawStatus);
  const resolved = hasBiometry !== null ? hasBiometry : normalizedStatus;

  if (resolved === true) {
    return {
      hasBiometry: true,
      message: "Biometria localizada na base da certificadora. Você pode seguir para o pagamento."
    };
  }

  if (resolved === false) {
    return {
      hasBiometry: false,
      message: "Não localizamos biometria na base da certificadora para este CPF."
    };
  }

  return {
    hasBiometry: null,
    message: rawStatus?.trim() ? rawStatus.trim() : undefined
  };
}

function extractPersonTypeFromResponse(data: any): "PF" | "PJ" | null {
  const keys = ["tipo_pessoa", "pessoa_tipo", "person_type", "personType"];
  const raw = deepFindFirstString(data, keys);
  const normalized = normalizeText(raw);
  if (!normalized) return null;
  if (normalized === "pf" || normalized === "fisica" || normalized === "pessoa fisica") return "PF";
  if (normalized === "pj" || normalized === "juridica" || normalized === "pessoa juridica") return "PJ";
  return null;
}

function formatApiDateToPtBr(value: string) {
  const normalized = String(value || "").trim();
  if (!/^\d{8}$/.test(normalized)) return normalized;
  return `${normalized.slice(6, 8)}/${normalized.slice(4, 6)}/${normalized.slice(0, 4)}`;
}

function getSchedulingTypeLabel(value: "VID" | "PRE" | "LIV" | null | undefined) {
  if (!value) return "";
  if (value === "PRE") return "Presencial";
  if (value === "LIV") return "Emita agora";
  return "Agendar horário";
}

function buildSelectionDisplayParts(certCategory: string, certModel: string, certValidity: string) {
  return [certCategory, certModel, certValidity].filter(Boolean);
}

function inferPersonTypeFromSelection(certCategory: string, productName: string) {
  const text = normalizeText(`${certCategory} ${productName}`);
  if (!text) return null;
  if (text.includes("cnpj") || text.includes("nf e") || text.includes("nfe") || text.includes("mei")) return "PJ";
  if (text.includes("cpf")) return "PF";
  return null;
}

function gtmPaymentMethodLabel(method: PaymentMethod) {
  if (method === "CREDIT_CARD") return "credit_card";
  if (method === "PIX") return "pix";
  if (method === "BOLETO") return "boleto";
  if (method === "LOJA") return "store";
  return String(method).toLowerCase();
}

export function CheckoutForm({ tenantSlug, defaultType, priceCents, whatsappLink }: Props) {
  const [spcertEnabled, setSpcertEnabled] = React.useState(false);
  const [method, setMethod] = React.useState<PaymentMethod>("PIX");
  const [type] = React.useState<"A1" | "A3">(defaultType);
  const [step, setStep] = React.useState<0 | 1 | 2 | 3>(0);
  const [remoteOrderId, setRemoteOrderId] = React.useState<string | null>(null);
  const paymentStepTracked = React.useRef(false);

  const searchParams = useSearchParams();
  const certCategory = searchParams.get("cert") ?? "";
  const certModel = searchParams.get("model") ?? "";
  const certValidity = searchParams.get("validity") ?? "";

  const [products, setProducts] = React.useState<ProductOption[]>([]);
  const [productId, setProductId] = React.useState<string>("");
  const [productName, setProductName] = React.useState<string>("");
  const [selectedPriceCents, setSelectedPriceCents] = React.useState<number | null>(priceCents);
  const [showProductPicker, setShowProductPicker] = React.useState(false);

  const [schedulingType, setSchedulingType] = React.useState<"VID" | "PRE" | "LIV" | null>(null);
  const [selectedDate, setSelectedDate] = React.useState(""); // YYYYMMDD (API)
  const [available, setAvailable] = React.useState<Array<{ id: number; label: string }>>([]);
  const [calendarId, setCalendarId] = React.useState<string>("");
  const [calendarYear, setCalendarYear] = React.useState(() => new Date().getFullYear());
  const [calendarMonth, setCalendarMonth] = React.useState(() => new Date().getMonth()); // 0..11
  const [monthCounts, setMonthCounts] = React.useState<Record<string, number>>({});
  const [monthLoading, setMonthLoading] = React.useState(false);

  const [personType, setPersonType] = React.useState<"PF" | "PJ">("PF");
  const [isAccountant, setIsAccountant] = React.useState<"Não" | "Sim">("Não");

  const [name, setName] = React.useState(""); // PF: nome completo | PJ: nome do responsável
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [birthDate, setBirthDate] = React.useState("");

  const [cpf, setCpf] = React.useState("");
  const [cnpj, setCnpj] = React.useState("");
  const [fantasyName, setFantasyName] = React.useState("");
  const [corporateName, setCorporateName] = React.useState("");

  const [cep, setCep] = React.useState("");
  const [street, setStreet] = React.useState("");
  const [number, setNumber] = React.useState("");
  const [district, setDistrict] = React.useState("");
  const [city, setCity] = React.useState("");
  const [uf, setUf] = React.useState("");
  const [complement, setComplement] = React.useState("");

  const [billingSame, setBillingSame] = React.useState(true);
  const [billCep, setBillCep] = React.useState("");
  const [billStreet, setBillStreet] = React.useState("");
  const [billNumber, setBillNumber] = React.useState("");
  const [billDistrict, setBillDistrict] = React.useState("");
  const [billCity, setBillCity] = React.useState("");
  const [billUf, setBillUf] = React.useState("");
  const [billComplement, setBillComplement] = React.useState("");

  const [cardNumber, setCardNumber] = React.useState("");
  const [cardHolder, setCardHolder] = React.useState("");
  const [cardExpiry, setCardExpiry] = React.useState("");
  const [cardCvv, setCardCvv] = React.useState("");
  const [focusField, setFocusField] = React.useState<string | null>(null);

  const [loadingCep, setLoadingCep] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Partial<Record<CheckoutFieldKey, string>>>({});

  const clearFieldError = React.useCallback((key: CheckoutFieldKey) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);
  const [paymentResult, setPaymentResult] = React.useState<PaymentResult | null>(null);
  const [spOrderNumber, setSpOrderNumber] = React.useState<string | null>(null);
  const [hasBiometry, setHasBiometry] = React.useState<boolean | null>(null);
  const [hasSynPassActive, setHasSynPassActive] = React.useState(false);
  const [biometryNotice, setBiometryNotice] = React.useState<string | null>(null);
  const [biometryChecking, setBiometryChecking] = React.useState(false);
  const [cnhDeclarationAccepted, setCnhDeclarationAccepted] = React.useState(false);
  const hoursSectionRef = React.useRef<HTMLDivElement | null>(null);

  const total = React.useMemo(() => selectedPriceCents ?? priceCents, [selectedPriceCents, priceCents]);

  const selectPaymentMethod = React.useCallback(
    (next: PaymentMethod) => {
      setMethod(next);
      pushCheckoutPaymentMethodSelected({
        payment_method: gtmPaymentMethodLabel(next),
        value: total / 100,
        currency: "BRL",
        product_name: productName || "Certificado Digital",
        tenant_slug: tenantSlug
      });
    },
    [total, productName, tenantSlug]
  );

  React.useEffect(() => {
    if (step !== 3) {
      paymentStepTracked.current = false;
      return;
    }
    if (paymentStepTracked.current) return;
    paymentStepTracked.current = true;
    pushCheckoutPaymentStep({
      payment_method: gtmPaymentMethodLabel(method),
      value: total / 100,
      currency: "BRL",
      product_name: productName || "Certificado Digital",
      tenant_slug: tenantSlug
    });
  }, [step, total, productName, tenantSlug, method]);
  const selectionFromLanding = Boolean(certCategory || certModel || certValidity);
  const selectedHourLabel = React.useMemo(
    () => available.find((item) => String(item.id) === calendarId)?.label ?? "",
    [available, calendarId]
  );
  const appointmentLabel = React.useMemo(() => {
    if (schedulingType === "LIV") return "Atendimento imediato";
    if (!selectedDate) return "";
    const dateLabel = formatApiDateToPtBr(selectedDate);
    return selectedHourLabel ? `${dateLabel} às ${selectedHourLabel}` : dateLabel;
  }, [selectedDate, selectedHourLabel, schedulingType]);
  const selectionDisplayParts = React.useMemo(
    () => buildSelectionDisplayParts(certCategory, certModel, certValidity),
    [certCategory, certModel, certValidity]
  );
  const selectionSummaryLabel = React.useMemo(() => {
    if (productName) return productName;
    const fromLanding = selectionDisplayParts.join(" • ");
    if (fromLanding) return fromLanding;
    return productName;
  }, [productName, selectionDisplayParts]);
  const summaryTitle = React.useMemo(() => {
    if (productName) return productName;
    if (selectionDisplayParts.length >= 2) return selectionDisplayParts.slice(0, 2).join(" • ");
    if (selectionDisplayParts.length === 1) return selectionDisplayParts[0];
    return type === "A1" ? "Certificado A1" : "Certificado A3";
  }, [productName, selectionDisplayParts, type]);
  const personTypeLabel = personType === "PF" ? "Pessoa física" : "Pessoa jurídica";
  const productSyncState = React.useMemo(() => {
    if (productId && productName) return { tone: "ready" as const, text: productName };
    if (productId) return { tone: "loading" as const, text: "Produto da SP CERTIFICADO DIGITAL selecionado." };
    if (selectionFromLanding) return { tone: "loading" as const, text: "Aguardando vínculo do produto da SP CERTIFICADO DIGITAL..." };
    return { tone: "idle" as const, text: "Selecione o produto da SP CERTIFICADO DIGITAL para continuar." };
  }, [productId, productName, selectionFromLanding]);

  React.useEffect(() => {
    const inferred = inferPersonTypeFromSelection(certCategory, productName);
    if (!inferred) return;
    setPersonType(inferred);
  }, [certCategory, productName]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/spcert/config", { cache: "no-store" });
        const data = await res.json();
        if (!res.ok) return;
        if (!cancelled) setSpcertEnabled(Boolean(data?.enabled));
      } catch {
        // se falhar, mantém false (fallback Safe2Pay)
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function fetchCep(normalizedCep: string) {
    setLoadingCep(true);
    setError(null);
    try {
      const res = await fetch(`/api/cep/${normalizedCep}`, { cache: "no-store" });
      if (!res.ok) throw new Error("CEP inválido");
      const data = (await res.json()) as {
        logradouro?: string;
        bairro?: string;
        localidade?: string;
        uf?: string;
        erro?: boolean;
      };
      if (data.erro) throw new Error("CEP não encontrado");
      setStreet(data.logradouro ?? "");
      setDistrict(data.bairro ?? "");
      setCity(data.localidade ?? "");
      setUf(data.uf ?? "");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao consultar CEP");
    } finally {
      setLoadingCep(false);
    }
  }

  React.useEffect(() => {
    const d = onlyDigits(cep);
    if (d.length === 8) void fetchCep(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cep]);

  React.useEffect(() => {
    if (!spcertEnabled) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/spcert/products", { cache: "no-store" });
        const data = await res.json();
        if (!res.ok) {
          const msg = typeof data?.message === "string" ? data.message : "Falha ao carregar produtos.";
          throw new Error(msg.length > 240 ? "Falha ao carregar produtos da SP CERTIFICADO DIGITAL." : msg);
        }
        const list: ProductOption[] = [];
        const arr = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : Array.isArray(data?.products) ? data.products : [];
        for (const it of arr) {
          const id = String(it?.id ?? it?.id_validade ?? it?.idValidade ?? "");
          const name = String(it?.name ?? it?.nome ?? it?.title ?? it?.descricao ?? `Produto ${id}`);
          if (id) {
            list.push({
              id,
              name,
              publicName: formatProductPublicName(name),
              priceCents: extractProductPriceCents(it)
            });
          }
        }
        if (!cancelled) {
          setProducts(list);
          if (!productId && list[0]?.id && !selectionFromLanding) {
            setProductId(list[0].id);
            setProductName(list[0]?.publicName ?? "");
            setSelectedPriceCents(list[0]?.priceCents ?? priceCents);
          }
        }
      } catch (e) {
        if (!cancelled) setError(getFriendlyCheckoutError(e instanceof Error ? e.message : "Falha ao carregar produtos."));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spcertEnabled]);

  React.useEffect(() => {
    if (!spcertEnabled) return;
    if (!selectionFromLanding) return;
    if (showProductPicker) return;
    if (productId) return;
    if (!products.length) return;

    const wantCategory = normalizeText(certCategory);
    const wantModel = normalizeText(certModel);
    const wantType = normalizeText(type);
    const wantValidity = normalizeText(certValidity);

    function scoreProduct(p: ProductOption) {
      const n = normalizeText(p.name);
      let score = 0;
      if (wantCategory && n.includes(wantCategory.replace(/\s+/g, ""))) score += 6;
      if (wantCategory && n.includes(wantCategory)) score += 6;
      if (wantType && n.includes(wantType)) score += 4;
      if (wantModel && (n.includes(wantModel) || n.includes(wantModel.replace(/\s+/g, "")))) score += 3;
      if (wantValidity) {
        if (wantValidity.includes("12") && (n.includes("12") || n.includes("12 meses") || n.includes("12meses"))) score += 2;
        if (wantValidity.includes("24") && (n.includes("24") || n.includes("24 meses") || n.includes("24meses"))) score += 2;
        if (wantValidity.includes("3") && (n.includes("3") || n.includes("3 meses") || n.includes("3meses"))) score += 2;
      }
      return score;
    }

    let best: ProductOption | null = null;
    let bestScore = -1;
    for (const p of products) {
      const s = scoreProduct(p);
      if (s > bestScore) {
        bestScore = s;
        best = p;
      }
    }

    if (best && (bestScore >= 6 || products.length === 1)) {
      setProductId(best.id);
      setProductName(best.publicName);
      setSelectedPriceCents(best.priceCents ?? priceCents);
      return;
    }

    setShowProductPicker(true);
  }, [spcertEnabled, selectionFromLanding, products, productId, certCategory, certModel, certValidity, type, showProductPicker, priceCents]);

  React.useEffect(() => {
    if (productId) clearFieldError("product");
  }, [productId, clearFieldError]);

  React.useEffect(() => {
    if (!productId) {
      setSelectedPriceCents(priceCents);
      return;
    }

    const selectedProduct = products.find((product) => product.id === productId);
    if (!selectedProduct) return;

    setProductName(selectedProduct.publicName);
    if (selectedProduct.priceCents != null) {
      setSelectedPriceCents(selectedProduct.priceCents);
      return;
    }

    const inferred = inferSelectionFromProductName(selectedProduct.name, type);
    if (!inferred) {
      setSelectedPriceCents(priceCents);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/checkout/product-price", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            tenantSlug,
            ...inferred
          })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.message ?? "Falha ao calcular o valor do produto.");
        if (!cancelled) setSelectedPriceCents(typeof data?.priceCents === "number" ? data.priceCents : priceCents);
      } catch {
        if (!cancelled) setSelectedPriceCents(priceCents);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [productId, products, tenantSlug, type, priceCents]);

  async function fetchAvailableHours(ymd: string, options?: { updateSelection?: boolean }) {
    const res = await fetch("/api/spcert/available-hours", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ selected_date: ymd })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message ?? "Falha ao buscar horários.");

    const opts = parseAvailableOptions(data);
    if (options?.updateSelection !== false) {
      setAvailable(opts);
      setCalendarId(opts[0]?.id ? String(opts[0].id) : "");
    }
    return opts.length;
  }

  React.useEffect(() => {
    if (!spcertEnabled) return;
    if (step !== 1) return;
    let cancelled = false;
    const cache: Record<string, number> = {};
    setMonthLoading(true);
    setAvailable([]);
    setCalendarId("");
    (async () => {
      const totalDays = daysInMonth(calendarYear, calendarMonth);
      for (let d = 1; d <= totalDays; d++) {
        if (cancelled) return;
        const ymd = toYmd(calendarYear, calendarMonth, d);
        if (monthCounts[ymd] != null) {
          cache[ymd] = monthCounts[ymd];
          continue;
        }
        try {
          const count = await fetchAvailableHours(ymd, { updateSelection: false });
          cache[ymd] = count;
        } catch {
          cache[ymd] = 0;
        }
        // throttle para respeitar 60 req/min
        await new Promise((r) => setTimeout(r, 220));
      }
      if (!cancelled) {
        setMonthCounts((prev) => ({ ...prev, ...cache }));
        setMonthLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spcertEnabled, step, calendarYear, calendarMonth]);

  React.useEffect(() => {
    if (!spcertEnabled || step !== 2) return;

    const cpfDigits = onlyDigits(cpf);
    if (cpfDigits.length !== 11) {
      setError("Informe um CPF válido (11 dígitos) para verificar a biometria.");
      setHasBiometry(null);
      setBiometryNotice(null);
      return;
    }

    let cancelled = false;
    setBiometryChecking(true);
    setHasBiometry(null);
    setBiometryNotice(null);
    setCnhDeclarationAccepted(false);
    setError(null);

    (async () => {
      try {
        const res = await fetch("/api/spcert/biometry/check", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ cpf: cpfDigits, order_id: remoteOrderId ?? undefined })
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(extractValidationMessage(data) ?? data?.message ?? "Falha ao consultar biometria.");
        }

        const biometryInfo = extractBiometryInfo(data?.raw ?? data);
        if (cancelled) return;

        setHasBiometry(biometryInfo.hasBiometry);
        setBiometryNotice(biometryInfo.message ?? null);

        if (biometryInfo.hasBiometry === true) {
          setStep(3);
        }
      } catch (e) {
        if (!cancelled) {
          setError(getFriendlyCheckoutError(e instanceof Error ? e.message : "Falha ao consultar biometria."));
        }
      } finally {
        if (!cancelled) setBiometryChecking(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [spcertEnabled, step, cpf, remoteOrderId]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (step !== 3) return;
    setSubmitting(true);
    setError(null);
    setPaymentResult(null);

    try {
      // validação mínima (evite bloat; coloque validação robusta no backend)
      if (!name.trim()) throw new Error("Informe seu nome.");
      if (!email.includes("@")) throw new Error("Informe um e-mail válido.");
      if (onlyDigits(phone).length < 10) throw new Error("Informe um telefone válido.");

      const doc = personType === "PF" ? onlyDigits(cpf) : onlyDigits(cnpj);
      if (doc.length < 11) throw new Error("Informe CPF/CNPJ válido.");

      if (personType === "PF" && !isValidBrDate(birthDate)) throw new Error("Informe uma data de nascimento válida.");
      if (personType === "PJ" && (!fantasyName.trim() || !corporateName.trim()))
        throw new Error("Informe Nome fantasia e Razão social.");

      if (onlyDigits(cep).length !== 8) throw new Error("Informe um CEP válido.");
      if (!street.trim() || !number.trim() || !district.trim() || !city.trim() || !uf.trim())
        throw new Error("Complete seu endereço.");

      if (method === "CREDIT_CARD") {
        if (onlyDigits(cardNumber).length < 13) throw new Error("Número do cartão inválido.");
        if (!cardHolder.trim()) throw new Error("Nome impresso no cartão é obrigatório.");
        if (!isValidCardExpiry(cardExpiry)) throw new Error("Validade inválida ou vencida (MM/AA).");
        if (onlyDigits(cardCvv).length < 3) throw new Error("CVV inválido.");
      }

      if (spcertEnabled) {
        const oid = remoteOrderId;
        if (!oid) throw new Error("Pedido não criado. Volte e conclua a etapa Identificação.");
          const resolvedOrderNumber = spOrderNumber ?? oid;

        // PIX: gerar QR code obrigatório
        if (method === "PIX") {
          const qrRes = await fetch("/api/spcert/payment/generate-pix-qrcode", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ order_id: oid, nome_loja: tenantSlug })
          });
          const qrData = await qrRes.json();
          if (!qrRes.ok) throw new Error(qrData?.message ?? "Falha ao gerar QR Code PIX.");
          const pixPresentation = extractPixPresentation(qrData);
          const pixOrderNumber = extractDisplayOrderFromResponse(qrData?.raw ?? qrData) ?? resolvedOrderNumber;
          const pixApiId = extractSpcertApiId(qrData?.raw ?? qrData) ?? extractSpcertApiId(qrData);
          setPaymentResult({
            kind: "pix",
            title: "Pedido criado com sucesso",
            description: "Agora falta apenas pagar o PIX para concluir a compra. Use o QR Code ou o código abaixo e acompanhe a confirmação automática.",
            qrCode: pixPresentation.qrCode,
            copyPaste: pixPresentation.copyPaste || JSON.stringify(qrData, null, 2),
            status: "Pendente",
            id: pixApiId ?? undefined,
            order: pixOrderNumber,
            name: selectionSummaryLabel || summaryTitle,
            atendimento: getSchedulingTypeLabel(schedulingType) || undefined,
            appointmentLabel: appointmentLabel || undefined,
            price: total / 100,
            biometryMessage: biometryNotice || undefined
          });
          return;
        }

        // Crédito / Boleto / Loja
        const finishPayload: any =
          method === "CREDIT_CARD"
            ? {
                order_id: oid,
                nome_loja: tenantSlug,
                payment_type: "credito",
                card_number: cardNumber,
                card_name: cardHolder,
                card_cvv: Number(onlyDigits(cardCvv)),
                card_expiration: (() => {
                  const [mm, yy] = cardExpiry.split("/");
                  // API exemplo pede MM/YYYY
                  return mm && yy ? `${mm}/20${yy}` : cardExpiry;
                })(),
                parcelamento: 1
              }
            : {
                order_id: oid,
                nome_loja: tenantSlug,
                payment_type: method === "BOLETO" ? "boleto" : "loja"
              };

        const payRes = await fetch("/api/spcert/payment/finish", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(finishPayload)
        });
        const payData = await payRes.json();
          if (!payRes.ok) throw new Error(getFriendlyCheckoutError(payData?.message ?? "Falha ao finalizar pagamento."));
        const paymentOrderNumber = extractDisplayOrderFromResponse(payData?.raw ?? payData) ?? resolvedOrderNumber;

        const boletoUrl = normalizeExternalUrl(payData?.url_boleto);
        if (boletoUrl) {
          setPaymentResult({
            kind: "boleto",
            title: "Boleto gerado",
            description: "Seu boleto foi emitido. Abra o PDF para visualizar, baixar ou imprimir.",
            actionUrl: boletoUrl,
            actionLabel: "Abrir boleto",
            copyPaste: boletoUrl,
            status: String(payData?.status ?? "Pendente"),
            id: payData?.id ? String(payData.id) : undefined,
            order: paymentOrderNumber,
            name: payData?.name ? String(payData.name) : selectionSummaryLabel || summaryTitle,
            category: payData?.category ? String(payData.category) : undefined,
            atendimento: payData?.atendimento ? String(payData.atendimento) : getSchedulingTypeLabel(schedulingType) || undefined,
            protocolo: payData?.protocolo ? String(payData.protocolo) : undefined,
            price: typeof payData?.price === "number" ? payData.price : total / 100,
            appointmentLabel: appointmentLabel || undefined,
            biometryMessage: biometryNotice || undefined
          });
        } else {
          setPaymentResult({
            kind: method === "LOJA" ? "loja" : "credito",
            title: method === "LOJA" ? "Pedido confirmado" : "Compra confirmada",
            description:
              method === "LOJA"
                ? "Seu pedido foi criado com atendimento em loja. Confira as informações abaixo para finalizar com tranquilidade."
                : "Recebemos sua compra e já deixamos as informações principais organizadas para o próximo passo.",
            status: String(payData?.status ?? "Pendente"),
            id: payData?.id ? String(payData.id) : undefined,
            order: paymentOrderNumber,
            name: payData?.name ? String(payData.name) : selectionSummaryLabel || summaryTitle,
            category: payData?.category ? String(payData.category) : undefined,
            atendimento: payData?.atendimento ? String(payData.atendimento) : getSchedulingTypeLabel(schedulingType) || undefined,
            protocolo: payData?.protocolo ? String(payData.protocolo) : undefined,
            price: typeof payData?.price === "number" ? payData.price : total / 100,
            appointmentLabel: appointmentLabel || undefined,
            biometryMessage: biometryNotice || undefined
          });
        }
      } else {
        // Safe2Pay (fluxo antigo)
        const payload = {
          tenantSlug,
          certificateType: type,
          certCategory,
          certModel,
          certValidity,
          amountCents: total,
          customer: {
            name,
            email,
            document: doc,
            phone: onlyDigits(phone)
          },
          address: {
            cep: onlyDigits(cep),
            street,
            number,
            district,
            city,
            uf,
            complement: complement || undefined
          },
          payment: {
            method: method === "CREDIT_CARD" ? "CREDIT_CARD" : "PIX",
            card:
              method === "CREDIT_CARD"
                ? {
                    number: onlyDigits(cardNumber),
                    holder: cardHolder,
                    expiry: cardExpiry, // MM/AA
                    cvv: onlyDigits(cardCvv)
                  }
                : undefined
          }
        };

        const res = await fetch("/api/checkout/create", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(payload)
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data?.message ?? "Falha ao criar pagamento.");

        if (method === "PIX") {
          setPaymentResult({
            kind: "pix",
            title: "PIX gerado",
            description: "Escaneie o QR Code ou copie o código abaixo para pagar.",
            qrCode: data.pix?.qrCode,
            copyPaste: data.pix?.copyPaste,
            status: String(data?.status ?? "PROCESSANDO")
          });
        } else {
          setPaymentResult({
            kind: "credito",
            title: "Pagamento enviado",
            description: "O pagamento foi enviado pelo checkout legado e está aguardando retorno.",
            status: String(data?.status ?? "PROCESSANDO")
          });
        }
      }
    } catch (e) {
      setError(getFriendlyCheckoutError(e instanceof Error ? e.message : "Erro no checkout"));
    } finally {
      setSubmitting(false);
    }
  }

  async function goNext() {
    setError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      if (!spcertEnabled) {
        setStep((s) => (s + 1) as 0 | 1 | 2 | 3);
        return;
      }

      if (step === 0) {
        const validation = validateCheckoutStep0({
          spcertEnabled,
          productId,
          personType,
          name,
          email,
          phone,
          cpf,
          cnpj,
          birthDate,
          fantasyName,
          corporateName,
          billingSame,
          cep,
          street,
          number,
          district,
          city,
          uf,
          billCep,
          billStreet,
          billNumber,
          billDistrict,
          billCity,
          billUf
        });
        if (Object.keys(validation.errors).length > 0) {
          setFieldErrors(validation.errors);
          setError(validation.summary);
          requestAnimationFrame(() => scrollToFirstCheckoutFieldError());
          return;
        }

        const billingCepValue = billingSame ? cep : billCep;
        const billingStreetValue = billingSame ? street : billStreet;
        const billingNumberValue = billingSame ? number : billNumber;
        const billingDistrictValue = billingSame ? district : billDistrict;
        const billingCityValue = billingSame ? city : billCity;
        const billingUfValue = billingSame ? uf : billUf;
        const billingComplementValue = billingSame ? complement : billComplement;
        const billingCepMasked = maskCep(billingCepValue);
        const billingCepDigits = onlyDigits(billingCepValue);
        const customerName = String(name).trim();
        const customerEmail = String(email).trim().toLowerCase();
        const customerPhoneMasked = maskPhone(phone);
        const customerPhoneIntl = formatCelularCobranca(phone);
        const customerCpfMasked = String(cpf).trim();
        const customerCpfDigits = onlyDigits(cpf);
        const customerCnpjMasked = personType === "PJ" ? String(cnpj).trim() : "";
        const customerCnpjDigits = personType === "PJ" ? onlyDigits(cnpj) : "";
        const customerBirthDate = String(birthDate).trim();
        const billingStreet = String(billingStreetValue).trim();
        const billingNumber = String(billingNumberValue).trim();
        const billingDistrict = String(billingDistrictValue).trim();
        const billingCity = String(billingCityValue).trim();
        const billingState = String(billingUfValue).trim().toUpperCase();
        const billingComplement = String(billingComplementValue ?? "").trim();

        if (
          !billingCepMasked.trim() ||
          !billingStreet ||
          !billingCity ||
          !billingDistrict ||
          !billingNumber ||
          !billingState
        )
          throw new Error("Complete o endereço de cobrança.");
        if (billingCepDigits.length !== 8) throw new Error("Informe um CEP válido.");
        if (billingState.length !== 2) throw new Error("Informe o estado com 2 letras.");

        const createPayload: any = {
          tenant_slug: tenantSlug,
          certificate_type: type,
          cert_kind: certCategory,
          cert_model: certModel,
          cert_validity: certValidity,
          id_validade: String(productId),
          nome_loja: tenantSlug,
          nome: customerName,
          nome_completo: customerName,
          nome_completo_cobranca: customerName,
          email: customerEmail,
          email_cobranca: customerEmail,
          celular: customerPhoneMasked,
          telefone: customerPhoneMasked,
          celular_cobranca: customerPhoneIntl,
          telefone_cobranca: customerPhoneMasked,
          celular_digits: onlyDigits(phone),
          cep: billingCepMasked,
          cep_digits: billingCepDigits,
          cep_cobranca: billingCepMasked,
          cep_cobranca_digits: billingCepDigits,
          logradouro_cobranca: billingStreet,
          cidade_cobranca: billingCity,
          estado_cobranca: billingState,
          bairro_cobranca: billingDistrict,
          numero_cobranca: billingNumber,
          complemento_cobranca: billingComplement,
          logradouro: billingStreet,
          cidade: billingCity,
          estado: billingState,
          bairro: billingDistrict,
          numero: billingNumber,
          complemento: billingComplement,
          pessoa_tipo: personType,
          tipo_pessoa: personType,
          fantasia: personType === "PJ" ? String(fantasyName) : "",
          nome_fantasia: personType === "PJ" ? String(fantasyName) : "",
          nome_fantasia_cobranca: personType === "PJ" ? String(fantasyName) : "",
          razao_social: personType === "PJ" ? String(corporateName) : "",
          razao_social_cobranca: personType === "PJ" ? String(corporateName) : "",
          is_contador: personType === "PJ" ? isAccountant === "Sim" : false,
          cnpj_cobranca: customerCnpjMasked,
          cnpj: customerCnpjMasked,
          cnpj_digits: customerCnpjDigits,
          cpf: customerCpfMasked,
          cpf_cobranca: customerCpfMasked,
          cpf_digits: customerCpfDigits,
          data_nascimento: customerBirthDate,
          data_nascimento_cobranca: customerBirthDate
        };

        const res = await fetch("/api/spcert/order/create", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(createPayload)
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(extractValidationMessage(data) ?? getFriendlyCheckoutError(data?.message ?? "Falha ao criar pedido."));
        }
        const orderId = extractOrderIdFromResponse(data);
        if (!orderId) throw new Error("Nao foi possivel iniciar seu pedido agora. Tente novamente em instantes.");
        const spOrder = extractDisplayOrderFromResponse(data?.raw ?? data) ?? String(orderId);
        const apiPayload = data?.raw ?? data;
        const apiPersonType = extractPersonTypeFromResponse(apiPayload);
        const synPassActive = Boolean(apiPayload?.has_syn_pass_active ?? apiPayload?.raw?.has_syn_pass_active);
        setRemoteOrderId(String(orderId));
        setSpOrderNumber(spOrder);
        setHasSynPassActive(synPassActive);
        setHasBiometry(null);
        setBiometryNotice(null);
        setCnhDeclarationAccepted(false);
        if (apiPersonType) setPersonType(apiPersonType);
        if (synPassActive) {
          setStep(3);
          return;
        }
        setStep(1);
        return;
      }

      if (step === 1) {
        if (!remoteOrderId) throw new Error("Pedido não criado.");
        if (!schedulingType) throw new Error("Selecione a forma de atendimento.");

        if (schedulingType === "LIV") {
          setSelectedDate("");
          setCalendarId("");
          setAvailable([]);
          setStep(2);
          return;
        }

        if (!selectedDate || selectedDate.length !== 8) throw new Error("Selecione um dia disponível.");
        if (!calendarId) throw new Error("Selecione um horário disponível.");

        const schedRes = await fetch("/api/spcert/schedule", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            scheduling_type: schedulingType,
            calendar_id: Number(calendarId),
            order_id: remoteOrderId
          })
        });
        const schedData = await schedRes.json();
        if (!schedRes.ok) throw new Error(extractValidationMessage(schedData) ?? schedData?.message ?? "Falha ao realizar agendamento.");
        setStep(2);
        return;
      }

      if (step === 2) {
        if (!remoteOrderId) throw new Error("Pedido não criado.");
        if (biometryChecking) throw new Error("Aguarde a verificação de biometria.");
        if (hasBiometry === null) throw new Error("Aguarde a verificação de biometria.");
        if (hasBiometry === true) {
          setStep(3);
          return;
        }
        if (!cnhDeclarationAccepted) {
          throw new Error("Confirme que possui CNH válida (emitida após 2017 — última via) para continuar.");
        }
        setStep(3);
      }
    } catch (e) {
      setError(getFriendlyCheckoutError(e instanceof Error ? e.message : "Erro"));
    } finally {
      setSubmitting(false);
    }
  }

  if (paymentResult) {
    const isPix = paymentResult.kind === "pix";
    const normalizedStatus = String(paymentResult.status ?? "").trim().toUpperCase();
    const isPaid = ["PAID", "PAGO", "APROVADO", "AUTORIZADO", "LIBERADO", "SUCCESS", "SUCESSO"].some((token) => normalizedStatus.includes(token));
    const isPending = !isPaid && ["PENDENTE", "PENDING", "PROCESSANDO", "EM ANALISE", "AGUARDANDO"].some((token) => normalizedStatus.includes(token));

    return (
      <Card
        style={{
          padding: 22,
          maxWidth: 920,
          margin: "0 auto",
          borderRadius: 24,
          border: "1px solid rgba(15,23,42,0.08)",
          boxShadow: "0 18px 44px rgba(15,23,42,0.08)"
        }}
      >
        <div style={{ display: "grid", gap: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ display: "grid", gap: 8 }}>
              <h2 style={{ margin: 0, letterSpacing: -0.4 }}>{paymentResult.title}</h2>
              <p style={{ margin: 0, color: "var(--muted)" }}>
                {paymentResult.description}
              </p>
            </div>
            {paymentResult.status ? (
              <span
                style={{
                  borderRadius: 999,
                  padding: "8px 12px",
                  fontSize: 12,
                  fontWeight: 900,
                  letterSpacing: 0.3,
                  background: isPaid ? "rgba(34,197,94,0.14)" : isPending ? "rgba(245,158,11,0.16)" : "rgba(15,23,42,0.08)",
                  color: isPaid ? "rgb(21,128,61)" : isPending ? "rgb(180,83,9)" : "rgba(15,23,42,0.82)"
                }}
              >
                {paymentResult.status}
              </span>
            ) : null}
          </div>

          {isPix ? (
            <div
              style={{
                display: "grid",
                gap: 16,
                gridTemplateColumns: "minmax(240px, 300px) minmax(0, 1fr)",
                alignItems: "stretch"
              }}
            >
              <div
                style={{
                  borderRadius: 22,
                  border: "1px solid rgba(15,23,42,0.08)",
                  background: "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96))",
                  padding: 16,
                  display: "grid",
                  gap: 12,
                  placeItems: "center"
                }}
              >
                {paymentResult.qrCode ? (
                  <Image
                    src={paymentResult.qrCode}
                    alt="QR Code PIX"
                    width={260}
                    height={260}
                    priority
                    unoptimized
                    style={{ borderRadius: 18, background: "#fff", padding: 10, width: "100%", height: "auto", maxWidth: 260 }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      maxWidth: 260,
                      aspectRatio: "1 / 1",
                      borderRadius: 18,
                      border: "1px dashed rgba(15,23,42,0.16)",
                      background: "rgba(248,250,252,0.92)",
                      display: "grid",
                      placeItems: "center",
                      color: "rgba(15,23,42,0.55)",
                      fontSize: 13,
                      textAlign: "center",
                      padding: 16
                    }}
                  >
                    QR Code indisponível nesta resposta.
                  </div>
                )}
                <div style={{ textAlign: "center", color: "rgba(15,23,42,0.68)", fontSize: 13.5, lineHeight: 1.5 }}>
                  Aponte a camera do app do seu banco para concluir o pagamento.
                </div>
              </div>

              <div style={{ display: "grid", gap: 14 }}>
                <div
                  style={{
                    borderRadius: 22,
                    border: "1px solid hsl(var(--primary) / 0.14)",
                    background: "linear-gradient(180deg, hsl(var(--primary) / 0.10), rgba(255,255,255,0.96))",
                    padding: 18,
                    display: "grid",
                    gap: 8
                  }}
                >
                  <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <span
                      style={{
                        borderRadius: 999,
                        padding: "6px 12px",
                        fontSize: 12,
                        fontWeight: 950,
                        background: "hsl(var(--primary) / 0.14)",
                        color: "rgb(21,128,61)"
                      }}
                    >
                      Compra quase concluída
                    </span>
                    <span style={{ color: "rgba(15,23,42,0.64)", fontWeight: 800, fontSize: 13 }}>
                      Falta apenas o pagamento do PIX
                    </span>
                  </div>
                  <div style={{ color: "rgba(15,23,42,0.74)", lineHeight: 1.7 }}>
                    O seu pedido já foi criado na SP CERTIFICADO DIGITAL. Pague o PIX abaixo para liberar a confirmação automática do pedido e seguir com a emissão.
                  </div>
                </div>

                {(paymentResult.order || paymentResult.id || paymentResult.name || paymentResult.atendimento || paymentResult.protocolo || paymentResult.appointmentLabel || typeof paymentResult.price === "number") ? (
                  <div
                    style={{
                      display: "grid",
                      gap: 10,
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))"
                    }}
                  >
                    {paymentResult.id ? (
                      <div className="card" style={{ padding: 14, borderRadius: 18 }}>
                        <div className="helper">ID SP CERTIFICADO DIGITAL</div>
                        <div style={{ fontWeight: 950 }}>{paymentResult.id}</div>
                      </div>
                    ) : null}
                    {paymentResult.order ? (
                      <div className="card" style={{ padding: 14, borderRadius: 18 }}>
                        <div className="helper">Pedido SP CERTIFICADO DIGITAL</div>
                        <div style={{ fontWeight: 950 }}>{paymentResult.order}</div>
                      </div>
                    ) : null}
                    {paymentResult.name ? (
                      <div className="card" style={{ padding: 14, borderRadius: 18 }}>
                        <div className="helper">Produto</div>
                        <div style={{ fontWeight: 950 }}>{paymentResult.name}</div>
                      </div>
                    ) : null}
                    {paymentResult.atendimento ? (
                      <div className="card" style={{ padding: 14, borderRadius: 18 }}>
                        <div className="helper">Atendimento</div>
                        <div style={{ fontWeight: 950 }}>{paymentResult.atendimento}</div>
                      </div>
                    ) : null}
                    {paymentResult.appointmentLabel ? (
                      <div className="card" style={{ padding: 14, borderRadius: 18 }}>
                        <div className="helper">Data e horário</div>
                        <div style={{ fontWeight: 950 }}>{paymentResult.appointmentLabel}</div>
                      </div>
                    ) : null}
                    {paymentResult.protocolo ? (
                      <div className="card" style={{ padding: 14, borderRadius: 18 }}>
                        <div className="helper">Protocolo</div>
                        <div style={{ fontWeight: 950 }}>{paymentResult.protocolo}</div>
                      </div>
                    ) : null}
                    {typeof paymentResult.price === "number" ? (
                      <div className="card" style={{ padding: 14, borderRadius: 18 }}>
                        <div className="helper">Valor</div>
                        <div style={{ fontWeight: 950 }}>
                          {paymentResult.price > 1000 ? `R$ ${paymentResult.price}` : formatBRLFromCents(Math.round(paymentResult.price * 100))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {paymentResult.copyPaste ? (
                  <div
                    style={{
                      borderRadius: 22,
                      border: "1px solid rgba(15,23,42,0.08)",
                      background: "rgba(248,250,252,0.72)",
                      padding: 16,
                      display: "grid",
                      gap: 10
                    }}
                  >
                    <label className="label" style={{ margin: 0 }}>
                      PIX Copia e Cola
                    </label>
                    <textarea
                      readOnly
                      value={paymentResult.copyPaste}
                      style={{
                        width: "100%",
                        minHeight: 132,
                        borderRadius: 16,
                        border: "1px solid rgba(15,23,42,0.14)",
                        background: "#fff",
                        color: "var(--text)",
                        padding: 12,
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, monospace",
                        fontSize: 13
                      }}
                    />
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <Button
                        onClick={async () => {
                          await navigator.clipboard.writeText(paymentResult.copyPaste ?? "");
                        }}
                      >
                        Copiar codigo
                      </Button>
                      <Button variant="ghost" onClick={() => setPaymentResult(null)}>
                        Voltar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 10 }}>
                    {paymentResult.biometryMessage ? (
                      <div
                        style={{
                          borderRadius: 18,
                          border: "1px solid hsl(var(--primary) / 0.18)",
                          background: "hsl(var(--primary) / 0.08)",
                          padding: 14,
                          color: "rgba(15,23,42,0.78)",
                          lineHeight: 1.6,
                          fontWeight: 800
                        }}
                      >
                        {paymentResult.biometryMessage}
                      </div>
                    ) : null}
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <Button variant="ghost" onClick={() => setPaymentResult(null)}>
                      Voltar
                    </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="checkoutSuccessWrap">
              <div className="checkoutSuccessHero">
                <div style={{ display: "grid", gap: 14 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        borderRadius: 999,
                        padding: "8px 14px",
                        background: "hsl(var(--primary) / 0.14)",
                        color: "rgb(21,128,61)",
                        fontWeight: 950,
                        fontSize: 12.5
                      }}
                    >
                      Compra confirmada
                    </span>
                    {paymentResult.status ? (
                      <span style={{ color: "rgba(15,23,42,0.62)", fontWeight: 800, fontSize: 13.5 }}>{paymentResult.status}</span>
                    ) : null}
                  </div>

                  <div style={{ display: "grid", gap: 8 }}>
                    <h2 style={{ margin: 0, letterSpacing: -0.6 }}>{paymentResult.title}</h2>
                    <p style={{ margin: 0, color: "rgba(15,23,42,0.72)", lineHeight: 1.7 }}>{paymentResult.description}</p>
                  </div>

                  <div className="checkoutSuccessGrid">
                    {paymentResult.order ? (
                      <div className="checkoutSuccessItem">
                        <div className="helper">Pedido SP CERTIFICADO DIGITAL</div>
                        <div style={{ fontWeight: 950 }}>{paymentResult.order}</div>
                      </div>
                    ) : null}
                    {paymentResult.appointmentLabel ? (
                      <div className="checkoutSuccessItem">
                        <div className="helper">Data e horário</div>
                        <div style={{ fontWeight: 950 }}>{paymentResult.appointmentLabel}</div>
                      </div>
                    ) : null}
                    {(paymentResult.atendimento || getSchedulingTypeLabel(schedulingType)) ? (
                      <div className="checkoutSuccessItem">
                        <div className="helper">Atendimento</div>
                        <div style={{ fontWeight: 950 }}>{paymentResult.atendimento ?? getSchedulingTypeLabel(schedulingType)}</div>
                      </div>
                    ) : null}
                    {paymentResult.name ? (
                      <div className="checkoutSuccessItem">
                        <div className="helper">Produto</div>
                        <div style={{ fontWeight: 950 }}>{paymentResult.name}</div>
                      </div>
                    ) : null}
                    {paymentResult.protocolo ? (
                      <div className="checkoutSuccessItem">
                        <div className="helper">Protocolo</div>
                        <div style={{ fontWeight: 950 }}>{paymentResult.protocolo}</div>
                      </div>
                    ) : null}
                    {typeof paymentResult.price === "number" ? (
                      <div className="checkoutSuccessItem">
                        <div className="helper">Valor</div>
                        <div style={{ fontWeight: 950 }}>
                          {paymentResult.price > 1000 ? `R$ ${paymentResult.price}` : formatBRLFromCents(Math.round(paymentResult.price * 100))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              {paymentResult.biometryMessage ? (
                <div
                  style={{
                    borderRadius: 22,
                    border: "1px solid hsl(var(--primary) / 0.18)",
                    background: "hsl(var(--primary) / 0.08)",
                    padding: 18,
                    color: "rgba(15,23,42,0.82)",
                    lineHeight: 1.65
                  }}
                >
                  <div style={{ fontWeight: 950, marginBottom: 6 }}>Biometria e validação</div>
                  <div>{paymentResult.biometryMessage}</div>
                </div>
              ) : null}

              <div
                style={{
                  borderRadius: 22,
                  border: "1px solid rgba(15,23,42,0.08)",
                  background: "rgba(255,255,255,0.92)",
                  padding: 18,
                  display: "grid",
                  gap: 10
                }}
              >
                <div style={{ fontWeight: 950, color: "var(--brand-2)" }}>Próximos passos</div>
                <div style={{ color: "rgba(15,23,42,0.72)", lineHeight: 1.7 }}>
                  {isPix
                    ? "Assim que o PIX for pago, a confirmação deve acontecer automaticamente. Guarde o número do pedido da SP CERTIFICADO DIGITAL exibido nesta tela para qualquer suporte."
                    : "Confira o número do pedido da SP CERTIFICADO DIGITAL acima e acompanhe o atendimento conforme a modalidade escolhida. Se houver qualquer pendência, nossa equipe consegue localizar rapidamente pelo pedido exibido nesta tela."}
                </div>
              </div>

              {paymentResult.copyPaste ? (
                <div
                  style={{
                    borderRadius: 22,
                    border: "1px solid rgba(15,23,42,0.08)",
                    background: "rgba(255,255,255,0.92)",
                    padding: 18,
                    display: "grid",
                    gap: 10
                  }}
                >
                  <label className="label" style={{ margin: 0 }}>
                    {paymentResult.kind === "boleto" ? "Link do boleto" : "Código"}
                  </label>
                  <textarea
                    readOnly
                    value={paymentResult.copyPaste}
                    style={{
                      width: "100%",
                      minHeight: 120,
                      borderRadius: 16,
                      border: "1px solid rgba(15,23,42,0.14)",
                      background: "rgba(255,255,255,0.92)",
                      color: "var(--text)",
                      padding: 12
                    }}
                  />
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <Button
                      onClick={async () => {
                        await navigator.clipboard.writeText(paymentResult.copyPaste ?? "");
                      }}
                    >
                      {paymentResult.kind === "boleto" ? "Copiar link" : "Copiar código"}
                    </Button>
                    {whatsappLink ? (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          window.open(whatsappLink, "_blank", "noopener,noreferrer");
                        }}
                      >
                        Falar no WhatsApp
                      </Button>
                    ) : null}
                    {paymentResult.actionUrl ? (
                      <Button
                        type="button"
                        onClick={() => {
                          window.open(paymentResult.actionUrl, "_blank", "noopener,noreferrer");
                        }}
                      >
                        {paymentResult.actionLabel ?? "Abrir"}
                      </Button>
                    ) : null}
                    <Button variant="ghost" onClick={() => setPaymentResult(null)}>
                      Voltar
                    </Button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {whatsappLink ? (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        window.open(whatsappLink, "_blank", "noopener,noreferrer");
                      }}
                    >
                      Falar no WhatsApp
                    </Button>
                  ) : null}
                  {paymentResult.actionUrl ? (
                    <Button
                      type="button"
                      onClick={() => {
                        window.open(paymentResult.actionUrl, "_blank", "noopener,noreferrer");
                      }}
                    >
                      {paymentResult.actionLabel ?? "Abrir"}
                    </Button>
                  ) : null}
                  <Button variant="ghost" onClick={() => setPaymentResult(null)}>
                    Voltar
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    );
  }

  return (
    <form onSubmit={onSubmit} className="checkoutFormLayout">
      <div
        className="card checkoutStepsBar"
        style={{
          padding: 12,
          borderRadius: 16,
          background: "linear-gradient(180deg, #fff, var(--surface-2))",
          border: "1px solid rgba(15,23,42,0.06)"
        }}
      >
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {(["Identificação", "Agendamento", "Biometria", "Pagamento"] as const).map((label, idx) => {
            const active = idx === step;
            const done = idx < step;
            return (
              <button
                key={label}
                type="button"
                onClick={() => {
                  if (idx <= step) setStep(idx as 0 | 1 | 2 | 3);
                }}
                className="btn"
                disabled={idx > step}
                style={{
                  padding: "10px 12px",
                  borderRadius: 999,
                  background: active ? "hsl(var(--primary) / 0.14)" : "rgba(15,23,42,0.04)",
                  color: active ? "var(--brand-2)" : "rgba(15,23,42,0.65)",
                  fontWeight: 950,
                  opacity: idx > step ? 0.56 : 1,
                  cursor: idx > step ? "not-allowed" : "pointer"
                }}
              >
                <span style={{ color: done ? "var(--brand)" : "rgba(15,23,42,0.35)", marginRight: 8 }}>
                  {done ? "✓" : idx + 1}
                </span>
                {label}
              </button>
            );
          })}
          <div style={{ flex: 1 }} />
          <div style={{ fontWeight: 950, color: "var(--brand-2)" }}>Total: {formatBRLFromCents(total)}</div>
        </div>
      </div>

      <div className="checkoutColumns">
        {/* COLUNA DIREITA (conteúdo por etapa — ordem visual via CSS no desktop) */}
        <Card className="checkoutFormColumn" style={{ padding: 0 }}>
          <div className="checkoutPanelHeader">
            <div className="checkoutPanelHeaderTitle">
              {step === 0 ? "DADOS PESSOAIS • Certificado" : step === 1 ? "AGENDAMENTO" : step === 2 ? "VERIFICAÇÃO DE BIOMETRIA" : "PAGAMENTO"}
            </div>
          </div>
          <div style={{ padding: 16, display: "grid", gap: 12 }}>
            {step === 0 ? (
              <>
                {spcertEnabled ? (
                  <div style={{ display: "grid", gap: 8 }}>
                    {selectionFromLanding && !showProductPicker ? (
                      <div
                        style={{
                          borderRadius: 18,
                          border: "1px solid hsl(var(--primary) / 0.18)",
                          background: "linear-gradient(180deg, hsl(var(--primary) / 0.08), rgba(255,255,255,0.98))",
                          boxShadow: "0 16px 30px hsl(var(--primary) / 0.08)",
                          padding: 14,
                          display: "grid",
                          gap: 8
                        }}
                      >
                        <div style={{ fontWeight: 950, color: "rgba(15,23,42,0.78)" }}>Certificado selecionado</div>
                        <div className="helper" style={{ margin: 0, color: "rgba(17,24,39,0.62)" }}>
                          <strong>{selectionSummaryLabel}</strong>
                        </div>
                        {productId ? (
                          <div className="helper" style={{ margin: 0, color: "rgba(17,24,39,0.62)" }}>
                            Produto SP CERTIFICADO DIGITAL: <strong>{productName}</strong>
                          </div>
                        ) : (
                          <div className="helper" style={{ margin: 0, color: "rgba(17,24,39,0.62)" }}>
                            Carregando produto da SP CERTIFICADO DIGITAL...
                          </div>
                        )}
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          <button
                            type="button"
                            className="btn btnGhost"
                            onClick={() => setShowProductPicker(true)}
                            style={{ padding: "10px 12px" }}
                          >
                            Alterar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <label className="label">Produto*</label>
                        <select
                          className="input"
                          value={productId}
                          onChange={(e) => {
                            const nextId = e.target.value;
                            setProductId(nextId);
                            const found = products.find((p) => p.id === nextId);
                            setProductName(found?.publicName ?? "");
                            setSelectedPriceCents(found?.priceCents ?? null);
                          }}
                          style={{ height: 48 }}
                        >
                          <option value="">Selecione</option>
                          {products.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.publicName}
                            </option>
                          ))}
                        </select>
                        <div className="helper" style={{ color: "rgba(17,24,39,0.55)" }}>
                          Selecione o produto correspondente ao certificado desejado.
                        </div>
                      </>
                    )}
                  </div>
                ) : null}

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ fontWeight: 900, color: "rgba(15,23,42,0.70)" }}>Selecione o tipo de pessoa</div>
                  <div style={{ flex: 1 }} />
                  <button
                    type="button"
                    className={personType === "PF" ? "btn btnPrimary" : "btn btnGhost"}
                    onClick={() => setPersonType("PF")}
                    style={{ padding: "10px 14px" }}
                  >
                    PF
                  </button>
                  <button
                    type="button"
                    className={personType === "PJ" ? "btn btnPrimary" : "btn btnGhost"}
                    onClick={() => setPersonType("PJ")}
                    style={{ padding: "10px 14px" }}
                  >
                    PJ
                  </button>
                </div>

                <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <Input
                      label={personType === "PF" ? "Nome Completo*" : "Nome do responsável*"}
                      value={name}
                      onChange={(e) => {
                        setName(sanitizeName(e.target.value));
                        clearFieldError("name");
                      }}
                      error={fieldErrors.name}
                      maxLength={80}
                      autoComplete="name"
                    />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <Input
                      label="E-mail*"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        clearFieldError("email");
                      }}
                      error={fieldErrors.email}
                      autoComplete="email"
                      inputMode="email"
                    />
                  </div>
                  <Input
                    label="Celular*"
                    value={phone}
                    onChange={(e) => {
                      setPhone(maskPhone(e.target.value));
                      clearFieldError("phone");
                    }}
                    error={fieldErrors.phone}
                    inputMode="tel"
                    autoComplete="tel"
                  />

                  {personType === "PF" ? (
                    <Input
                      label="CPF*"
                      value={cpf}
                      onChange={(e) => {
                        setCpf(maskCpfCnpj(e.target.value));
                        clearFieldError("cpf");
                      }}
                      error={fieldErrors.cpf}
                      maxLength={14}
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  ) : (
                    <Input
                      label="CNPJ*"
                      value={cnpj}
                      onChange={(e) => {
                        setCnpj(maskCpfCnpj(e.target.value));
                        clearFieldError("cnpj");
                      }}
                      error={fieldErrors.cnpj}
                      maxLength={18}
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  )}

                  {personType === "PF" ? (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <Input
                        label="Data de nascimento* (dd/mm/aaaa)"
                        value={birthDate}
                        onChange={(e) => {
                          setBirthDate(maskBirthDate(e.target.value));
                          clearFieldError("birthDate");
                        }}
                        error={fieldErrors.birthDate}
                        maxLength={10}
                        inputMode="numeric"
                        placeholder="dd/mm/aaaa"
                        autoComplete="bday"
                      />
                    </div>
                  ) : (
                    <>
                      <Input
                        label="CPF do responsável*"
                        value={cpf}
                        onChange={(e) => {
                          setCpf(maskCpfCnpj(e.target.value));
                          clearFieldError("cpf");
                        }}
                        error={fieldErrors.cpf}
                        maxLength={14}
                        inputMode="numeric"
                        autoComplete="off"
                      />
                      <Input
                        label="Data de nascimento do responsável* (dd/mm/aaaa)"
                        value={birthDate}
                        onChange={(e) => {
                          setBirthDate(maskBirthDate(e.target.value));
                          clearFieldError("birthDate");
                        }}
                        error={fieldErrors.birthDate}
                        maxLength={10}
                        inputMode="numeric"
                        placeholder="dd/mm/aaaa"
                        autoComplete="bday"
                      />
                      <Input
                        label="Nome fantasia*"
                        value={fantasyName}
                        onChange={(e) => {
                          setFantasyName(e.target.value);
                          clearFieldError("fantasyName");
                        }}
                        error={fieldErrors.fantasyName}
                        autoComplete="organization"
                      />
                      <Input
                        label="Razão social*"
                        value={corporateName}
                        onChange={(e) => {
                          setCorporateName(e.target.value);
                          clearFieldError("corporateName");
                        }}
                        error={fieldErrors.corporateName}
                        autoComplete="organization"
                      />
                      <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 900, color: "rgba(15,23,42,0.70)" }}>Você é contador?</div>
                        <div style={{ flex: 1 }} />
                        <button
                          type="button"
                          className={isAccountant === "Não" ? "btn btnPrimary" : "btn btnGhost"}
                          onClick={() => setIsAccountant("Não")}
                          style={{ padding: "10px 14px" }}
                        >
                          Não
                        </button>
                        <button
                          type="button"
                          className={isAccountant === "Sim" ? "btn btnPrimary" : "btn btnGhost"}
                          onClick={() => setIsAccountant("Sim")}
                          style={{ padding: "10px 14px" }}
                        >
                          Sim
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <div style={{ marginTop: 6, fontWeight: 950, color: "var(--brand-2)" }}>Cobrança e nota fiscal</div>
                <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
                  <Input
                    label="CEP*"
                    value={cep}
                    onChange={(e) => {
                      setCep(maskCep(e.target.value));
                      clearFieldError("cep");
                    }}
                    error={fieldErrors.cep}
                    maxLength={9}
                    inputMode="numeric"
                    autoComplete="postal-code"
                    helperText={loadingCep ? "Consultando CEP..." : undefined}
                  />
                  <Input
                    label="Endereço*"
                    value={street}
                    onChange={(e) => {
                      setStreet(e.target.value);
                      clearFieldError("street");
                    }}
                    error={fieldErrors.street}
                  />
                  <Input
                    label="Número*"
                    value={number}
                    onChange={(e) => {
                      setNumber(e.target.value);
                      clearFieldError("number");
                    }}
                    error={fieldErrors.number}
                    inputMode="numeric"
                  />
                  <Input
                    label="Bairro*"
                    value={district}
                    onChange={(e) => {
                      setDistrict(e.target.value);
                      clearFieldError("district");
                    }}
                    error={fieldErrors.district}
                  />
                  <Input
                    label="Cidade*"
                    value={city}
                    onChange={(e) => {
                      setCity(e.target.value);
                      clearFieldError("city");
                    }}
                    error={fieldErrors.city}
                  />
                  <Input
                    label="Estado*"
                    value={uf}
                    onChange={(e) => {
                      setUf(e.target.value.toUpperCase().slice(0, 2));
                      clearFieldError("uf");
                    }}
                    error={fieldErrors.uf}
                  />
                  <div style={{ gridColumn: "1 / -1" }}>
                    <Input label="Complemento (Opcional)" value={complement} onChange={(e) => setComplement(e.target.value)} />
                  </div>
                </div>

                <label style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 6, color: "rgba(15,23,42,0.75)", fontWeight: 900 }}>
                  <input type="checkbox" checked={billingSame} onChange={(e) => setBillingSame(e.target.checked)} />
                  Utilizar dados acima para cobrança e nota fiscal
                </label>

                {!billingSame ? (
                  <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
                    <div style={{ gridColumn: "1 / -1", fontWeight: 950, color: "var(--brand-2)" }}>
                      Endereço de cobrança (alternativo)
                    </div>
                    <Input
                      label="CEP"
                      value={billCep}
                      onChange={(e) => {
                        setBillCep(maskCep(e.target.value));
                        clearFieldError("billCep");
                      }}
                      error={fieldErrors.billCep}
                      inputMode="numeric"
                    />
                    <Input
                      label="Endereço"
                      value={billStreet}
                      onChange={(e) => {
                        setBillStreet(e.target.value);
                        clearFieldError("billStreet");
                      }}
                      error={fieldErrors.billStreet}
                    />
                    <Input
                      label="Número"
                      value={billNumber}
                      onChange={(e) => {
                        setBillNumber(e.target.value);
                        clearFieldError("billNumber");
                      }}
                      error={fieldErrors.billNumber}
                      inputMode="numeric"
                    />
                    <Input
                      label="Bairro"
                      value={billDistrict}
                      onChange={(e) => {
                        setBillDistrict(e.target.value);
                        clearFieldError("billDistrict");
                      }}
                      error={fieldErrors.billDistrict}
                    />
                    <Input
                      label="Cidade"
                      value={billCity}
                      onChange={(e) => {
                        setBillCity(e.target.value);
                        clearFieldError("billCity");
                      }}
                      error={fieldErrors.billCity}
                    />
                    <Input
                      label="Estado"
                      value={billUf}
                      onChange={(e) => {
                        setBillUf(e.target.value.toUpperCase().slice(0, 2));
                        clearFieldError("billUf");
                      }}
                      error={fieldErrors.billUf}
                    />
                    <div style={{ gridColumn: "1 / -1" }}>
                      <Input label="Complemento (Opcional)" value={billComplement} onChange={(e) => setBillComplement(e.target.value)} />
                    </div>
                  </div>
                ) : null}
              </>
            ) : step === 1 ? (
              <>
                {spcertEnabled ? (
                  <>
                    <p style={{ margin: 0, color: "rgba(17,24,39,0.70)", lineHeight: 1.7 }}>
                      Selecione a melhor forma de atendimento e depois escolha o melhor dia e horário para seguir com a emissão.
                    </p>

                    <div style={{ display: "grid", gap: 12 }}>
                      <div style={{ display: "grid", gap: 10 }}>
                        <label className="label">Forma de atendimento</label>
                        <div className="scheduleModeGrid">
                          {[
                            {
                              value: "LIV" as const,
                              title: "Emita agora",
                              subtitle: "Videochamada com destaque imediato",
                              pulse: true
                            },
                            {
                              value: "VID" as const,
                              title: "Agendar horário",
                              subtitle: "Escolha o melhor horário para atendimento"
                            },
                            {
                              value: "PRE" as const,
                              title: "Presencial",
                              subtitle: "Atendimento presencial na unidade"
                            }
                          ].map((item) => {
                            const active = schedulingType === item.value;
                            return (
                              <button
                                key={item.value}
                                type="button"
                                className={[
                                  "scheduleModeCard",
                                  active ? "scheduleModeCardActive" : "",
                                  active && item.pulse ? "scheduleModeCardPulse" : ""
                                ].join(" ")}
                                onClick={() => setSchedulingType(item.value)}
                              >
                                <div style={{ display: "grid", gap: 4 }}>
                                  <div style={{ fontWeight: 950, color: "var(--brand-2)" }}>{item.title}</div>
                                  <div style={{ color: "rgba(17,24,39,0.62)", fontSize: 13, lineHeight: 1.5 }}>{item.subtitle}</div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {!schedulingType ? (
                        <div
                          style={{
                            borderRadius: 18,
                            border: "1px solid rgba(15,23,42,0.10)",
                            background: "linear-gradient(180deg, rgba(248,250,252,0.98), rgba(255,255,255,0.98))",
                            padding: 16,
                            color: "rgba(15,23,42,0.70)",
                            lineHeight: 1.65
                          }}
                        >
                          <div style={{ fontWeight: 950, color: "rgba(15,23,42,0.78)" }}>Escolha primeiro a forma de atendimento</div>
                          <div style={{ marginTop: 6 }}>
                            Depois da escolha, o calendário ou o fluxo imediato será exibido automaticamente.
                          </div>
                        </div>
                      ) : schedulingType === "LIV" ? (
                        <div
                          style={{
                            borderRadius: 18,
                            border: "1px solid hsl(var(--primary) / 0.18)",
                            background: "linear-gradient(180deg, hsl(var(--primary) / 0.10), rgba(255,255,255,0.98))",
                            padding: 16,
                            color: "rgba(15,23,42,0.78)",
                            lineHeight: 1.65
                          }}
                        >
                          <div style={{ fontWeight: 950, color: "var(--brand-2)" }}>Atendimento imediato selecionado</div>
                          <div style={{ marginTop: 6 }}>
                            Nesse modo, você pode seguir sem escolher data e horário. O atendimento será tratado como prioridade imediata.
                          </div>
                        </div>
                      ) : (
                      <div className="calWrap">
                        <div className="calHeader">
                          <button
                            type="button"
                            className="btn btnGhost"
                            onClick={() => {
                              const prev = calendarMonth - 1;
                              if (prev < 0) {
                                setCalendarMonth(11);
                                setCalendarYear((y) => y - 1);
                              } else setCalendarMonth(prev);
                            }}
                            style={{ padding: "10px 12px" }}
                            aria-label="Mês anterior"
                          >
                            ←
                          </button>
                          <div className="calTitle">
                            {MONTHS[calendarMonth]} / {calendarYear}
                          </div>
                          <button
                            type="button"
                            className="btn btnGhost"
                            onClick={() => {
                              const next = calendarMonth + 1;
                              if (next > 11) {
                                setCalendarMonth(0);
                                setCalendarYear((y) => y + 1);
                              } else setCalendarMonth(next);
                            }}
                            style={{ padding: "10px 12px" }}
                            aria-label="Próximo mês"
                          >
                            →
                          </button>
                        </div>

                <div className="calGrid" style={{ marginBottom: 6 }}>
                  {DOW.map((d) => (
                    <div key={d} className="calDow">
                      {d}
                    </div>
                  ))}
                </div>

                <div className="calGrid">
                  {Array.from({ length: firstDow(calendarYear, calendarMonth) }).map((_, i) => (
                    <div key={`b-${i}`} />
                  ))}
                  {Array.from({ length: daysInMonth(calendarYear, calendarMonth) }).map((_, i) => {
                    const day = i + 1;
                    const ymd = toYmd(calendarYear, calendarMonth, day);
                    const count = monthCounts[ymd] ?? 0;
                    const disabled = !monthLoading && count === 0;
                    const selected = selectedDate === ymd;
                    const dotClass =
                      count >= 5 ? "calDot calDotGreen" : count >= 1 ? "calDot calDotOrange" : "";
                    const availabilityClass =
                      count >= 5
                        ? "calDayHighAvailability"
                        : count >= 1
                          ? "calDayLowAvailability"
                          : "calDayNoAvailability";

                    return (
                      <button
                        key={ymd}
                        type="button"
                        className={[
                          "calDay",
                          availabilityClass,
                          disabled ? "calDayDisabled" : "",
                          selected ? "calDaySelected" : ""
                        ].join(" ")}
                        onClick={async () => {
                          if (disabled) return;
                          setSelectedDate(ymd);
                          try {
                            const hoursCount = await fetchAvailableHours(ymd);
                            if (hoursCount > 0) {
                              requestAnimationFrame(() => {
                                hoursSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                              });
                            }
                          } catch (err) {
                            setAvailable([]);
                            setCalendarId("");
                            setError(getFriendlyCheckoutError(err instanceof Error ? err.message : "Falha ao buscar horários."));
                          }
                        }}
                        aria-label={`Dia ${day}`}
                      >
                        {day}
                        {dotClass ? <span className={dotClass} aria-hidden="true" /> : null}
                      </button>
                    );
                  })}
                </div>

                <div style={{ marginTop: 10, color: "rgba(17,24,39,0.58)", fontSize: 13, fontWeight: 800 }}>
                  {monthLoading ? "Carregando disponibilidade do mês..." : "Clique em um dia para ver os horários."}
                </div>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8, color: "rgba(17,24,39,0.62)", fontSize: 12, fontWeight: 800 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span className="calDot calDotGreen" />
                    Muitos horários
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span className="calDot calDotOrange" />
                    Poucos horários
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span
                      aria-hidden="true"
                      style={{ width: 9, height: 9, borderRadius: 999, background: "rgba(148,163,184,0.8)", display: "inline-block" }}
                    />
                    Sem horários
                  </span>
                </div>
              </div>
                      )}

                      {selectedDate && available.length > 0 ? (
                        <div ref={hoursSectionRef}>
                          <div style={{ fontWeight: 950, color: "rgba(15,23,42,0.78)" }}>Horários do dia</div>
                          <p style={{ margin: "6px 0 0", color: "rgba(17,24,39,0.64)", lineHeight: 1.65 }}>
                            Selecione o melhor horário para atendimento.
                          </p>
                          <div className="timeSlots">
                            {available.map((h) => (
                              <button
                                key={h.id}
                                type="button"
                                className={["timeSlot", calendarId === String(h.id) ? "timeSlotSelected" : ""].join(" ")}
                                onClick={() => setCalendarId(String(h.id))}
                              >
                                {h.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <>
                    <p style={{ margin: 0, color: "rgba(17,24,39,0.70)", lineHeight: 1.7 }}>
                      A escolha de data e horário será confirmada após a finalização do pedido.
                    </p>
                    <div
                      style={{
                        borderRadius: 16,
                        border: "1px solid rgba(249,115,22,0.18)",
                        background: "rgba(249,115,22,0.08)",
                        color: "rgba(15,23,42,0.78)",
                        padding: 14,
                        fontWeight: 800,
                        lineHeight: 1.6
                      }}
                    >
                      Nossa equipe vai entrar em contato para confirmar o melhor dia e horário de atendimento.
                    </div>
                  </>
                )}
              </>
            ) : step === 2 ? (
              <>
                <p style={{ margin: 0, color: "rgba(17,24,39,0.70)", lineHeight: 1.7 }}>
                  Consultamos na base da certificadora se já existe biometria cadastrada para o CPF informado.
                </p>

                {spcertEnabled ? (
                  <>
                    {biometryChecking ? (
                      <div
                        style={{
                          borderRadius: 18,
                          border: "1px solid hsl(var(--primary) / 0.18)",
                          background: "hsl(var(--primary) / 0.06)",
                          padding: 14,
                          color: "rgba(15,23,42,0.78)",
                          fontWeight: 800
                        }}
                      >
                        Verificando biometria…
                      </div>
                    ) : null}

                    {biometryNotice && hasBiometry === false ? (
                      <div
                        role="alert"
                        style={{
                          borderRadius: 18,
                          border: "1px solid rgba(220, 38, 38, 0.45)",
                          background: "#f87171",
                          padding: 14,
                          color: "#ffffff",
                          lineHeight: 1.6,
                          fontWeight: 800,
                          boxShadow: "0 8px 24px rgba(239, 68, 68, 0.22)"
                        }}
                      >
                        {biometryNotice}
                      </div>
                    ) : null}

                    {hasBiometry === false && !biometryChecking ? (
                      <label
                        style={{
                          display: "grid",
                          gridTemplateColumns: "auto 1fr",
                          gap: 12,
                          alignItems: "start",
                          padding: 14,
                          borderRadius: 18,
                          border: cnhDeclarationAccepted
                            ? "1px solid hsl(var(--primary) / 0.35)"
                            : "1px solid rgba(15,23,42,0.12)",
                          background: cnhDeclarationAccepted ? "hsl(var(--primary) / 0.06)" : "rgba(15,23,42,0.02)",
                          cursor: "pointer",
                          lineHeight: 1.65,
                          fontWeight: 700,
                          color: "rgba(15,23,42,0.82)"
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={cnhDeclarationAccepted}
                          onChange={(e) => {
                            setCnhDeclarationAccepted(e.target.checked);
                            if (e.target.checked) setError(null);
                          }}
                          style={{ marginTop: 4, width: 18, height: 18, accentColor: "hsl(var(--primary))" }}
                        />
                        <span>
                          Declaro que não foi localizada biometria na base da certificadora e que, para esta emissão, será
                          necessária a apresentação de CNH emitida após 2017 (última via). Confirmo que possuo este documento e
                          apresentarei na validação.
                        </span>
                      </label>
                    ) : null}
                  </>
                ) : (
                  <p style={{ margin: 0, color: "rgba(17,24,39,0.65)", lineHeight: 1.7 }}>
                    Continue para escolher a forma de pagamento.
                  </p>
                )}
              </>
            ) : (
              <>
                <div style={{ display: "grid", gap: 12 }}>
                  {method === "CREDIT_CARD" ? (
                    <CardPreview
                      number={cardNumber}
                      name={cardHolder}
                      expiry={cardExpiry}
                      cvv={cardCvv}
                      flipped={focusField === "cvv"}
                    />
                  ) : null}

                  <div style={{ display: "grid", gap: 10 }}>
                    <label className="label">Pagamento</label>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {spcertEnabled ? (
                        <>
                          <Button
                            type="button"
                            variant={method === "CREDIT_CARD" ? "primary" : "ghost"}
                            onClick={() => selectPaymentMethod("CREDIT_CARD")}
                          >
                            Crédito
                          </Button>
                          <Button type="button" variant={method === "PIX" ? "primary" : "ghost"} onClick={() => selectPaymentMethod("PIX")}>
                            Pix
                          </Button>
                          <Button type="button" variant={method === "BOLETO" ? "primary" : "ghost"} onClick={() => selectPaymentMethod("BOLETO")}>
                            Boleto
                          </Button>
                          <Button type="button" variant={method === "LOJA" ? "primary" : "ghost"} onClick={() => selectPaymentMethod("LOJA")}>
                            Loja
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button type="button" variant={method === "PIX" ? "primary" : "ghost"} onClick={() => selectPaymentMethod("PIX")}>
                            PIX
                          </Button>
                          <Button
                            type="button"
                            variant={method === "CREDIT_CARD" ? "primary" : "ghost"}
                            onClick={() => selectPaymentMethod("CREDIT_CARD")}
                          >
                            Cartão
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {method === "CREDIT_CARD" ? (
                    <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <Input
                          label="Número do cartão"
                          value={cardNumber}
                          onChange={(e) => setCardNumber(maskCardNumber(e.target.value))}
                          maxLength={19}
                          onFocus={() => setFocusField("number")}
                          onBlur={() => setFocusField(null)}
                          inputMode="numeric"
                          autoComplete="cc-number"
                        />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <Input
                          label="Seu nome impresso no cartão"
                          value={cardHolder}
                          onChange={(e) => setCardHolder(sanitizeName(e.target.value).toUpperCase())}
                          maxLength={26}
                          onFocus={() => setFocusField("name")}
                          onBlur={() => setFocusField(null)}
                          autoComplete="cc-name"
                        />
                      </div>
                      <Input
                        label="Validade (MM/AA)"
                        value={cardExpiry}
                        onChange={(e) => setCardExpiry(maskExpiry(e.target.value))}
                        maxLength={5}
                        onFocus={() => setFocusField("expiry")}
                        onBlur={() => setFocusField(null)}
                        inputMode="numeric"
                        autoComplete="cc-exp"
                      />
                      <Input
                        label="CVV"
                        value={cardCvv}
                        onChange={(e) => setCardCvv(onlyDigits(e.target.value).slice(0, 4))}
                        maxLength={4}
                        onFocus={() => setFocusField("cvv")}
                        onBlur={() => setFocusField(null)}
                        inputMode="numeric"
                        autoComplete="cc-csc"
                      />
                    </div>
                  ) : (
                    <div style={{ color: "rgba(17,24,39,0.72)", fontSize: 13.5, lineHeight: 1.6 }}>
                      {spcertEnabled ? (
                        <>
                          Ao clicar em <strong>Comprar Agora</strong>, seu pedido será finalizado e você verá o status.
                          {method === "BOLETO" ? " Se for boleto, exibiremos o link para download." : null}
                        </>
                      ) : (
                        <>
                          Ao clicar em <strong>Comprar Agora</strong>, você verá o QR Code e o PIX Copia/Cola na próxima tela.
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </Card>

        {/* COLUNA ESQUERDA (resumo fixo — ordem visual via CSS no desktop) */}
        <Card className="checkoutSummaryColumn" style={{ padding: 0 }}>
          <div className="checkoutPanelHeader">
            <div className="checkoutPanelHeaderTitle">Resumo</div>
          </div>
          <div style={{ padding: 16, display: "grid", gap: 12 }}>
            <div style={{ marginTop: 8, border: "1px solid rgba(15,23,42,0.10)", borderRadius: 12, overflow: "hidden" }}>
              <div
                style={{
                  padding: 12,
                  background: "linear-gradient(180deg, hsl(var(--primary) / 0.08), rgba(255,255,255,0.98))",
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: 900
                }}
              >
                <span>{summaryTitle}</span>
                <span>{formatBRLFromCents(total)}</span>
              </div>
              {selectionFromLanding ? (
                <div style={{ padding: "10px 12px", borderTop: "1px solid rgba(15,23,42,0.08)", color: "rgba(15,23,42,0.68)", fontSize: 13.5, fontWeight: 800 }}>
                  {selectionSummaryLabel}
                </div>
              ) : null}
              <div
                style={{
                  padding: "10px 12px",
                  borderTop: "1px solid rgba(15,23,42,0.08)",
                  display: "grid",
                  gap: 8,
                  background: "rgba(248,250,252,0.72)"
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, color: "rgba(15,23,42,0.72)", fontSize: 13.5 }}>
                  <span style={{ fontWeight: 800 }}>Tipo de pessoa</span>
                  <strong style={{ color: "rgba(15,23,42,0.86)" }}>{personTypeLabel}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, color: "rgba(15,23,42,0.72)", fontSize: 13.5 }}>
                  <span style={{ fontWeight: 800 }}>Atendimento</span>
                  <strong style={{ color: "rgba(15,23,42,0.86)" }}>{getSchedulingTypeLabel(schedulingType) || "A definir"}</strong>
                </div>
                <div
                  className={fieldErrors.product ? "checkoutProductError" : undefined}
                  style={{
                    borderRadius: 12,
                    padding: "10px 12px",
                    border: fieldErrors.product
                      ? "1px solid hsl(0 72% 51% / 0.45)"
                      : productSyncState.tone === "ready"
                        ? "1px solid hsl(var(--primary) / 0.18)"
                        : "1px solid rgba(15,23,42,0.08)",
                    background: fieldErrors.product
                      ? "hsl(0 72% 51% / 0.08)"
                      : productSyncState.tone === "ready"
                        ? "hsl(var(--primary) / 0.08)"
                        : productSyncState.tone === "loading"
                          ? "rgba(248,250,252,0.92)"
                          : "rgba(255,255,255,0.88)"
                  }}
                >
                  <div className="helper" style={{ margin: 0 }}>
                    Produto SP CERTIFICADO DIGITAL
                  </div>
                  <div style={{ marginTop: 4, color: "rgba(15,23,42,0.82)", fontWeight: 900 }}>{productSyncState.text}</div>
                  {fieldErrors.product ? (
                    <div className="fieldError" style={{ marginTop: 6 }} role="alert">
                      {fieldErrors.product}
                    </div>
                  ) : null}
                </div>
              </div>
              <div style={{ padding: 12, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span style={{ fontWeight: 950, fontSize: 16 }}>Total</span>
                <span style={{ fontWeight: 950, fontSize: 18 }}>{formatBRLFromCents(total)}</span>
              </div>
            </div>

            {error ? (
              <div
                role="alert"
                style={{
                  color: "#b91c1c",
                  background: "rgba(239,68,68,0.10)",
                  padding: 12,
                  borderRadius: 12,
                  border: "1px solid rgba(239,68,68,0.20)"
                }}
              >
                {error}
              </div>
            ) : null}

            <div style={{ display: "grid", gap: 10 }}>
              {step > 0 ? (
                <Button type="button" variant="ghost" onClick={() => setStep((s) => (s - 1) as 0 | 1 | 2 | 3)}>
                  Voltar
                </Button>
              ) : null}

              {step < 3 ? (
                <Button
                  type="button"
                  onClick={goNext}
                  disabled={submitting || (step === 2 && spcertEnabled && (biometryChecking || hasBiometry === true))}
                  style={{ width: "100%", justifyContent: "center" }}
                >
                  {submitting || (step === 2 && biometryChecking) ? "Aguarde..." : "Continuar"}
                </Button>
              ) : (
                <Button disabled={submitting} type="submit" style={{ width: "100%", justifyContent: "center" }}>
                  {submitting ? "Processando..." : "Comprar Agora"}
                </Button>
              )}
            </div>

            <div
              style={{
                marginTop: 2,
                display: "flex",
                gap: 10,
                alignItems: "center",
                justifyContent: "center",
                padding: 10,
                borderRadius: 12,
                background: "hsl(var(--primary) / 0.10)",
                border: "1px solid hsl(var(--primary) / 0.18)",
                color: "rgba(15,23,42,0.75)",
                fontSize: 12,
                fontWeight: 900
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M7 11V8a5 5 0 0 1 10 0v3"
                  stroke="rgba(15,23,42,0.75)"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <rect
                  x="6"
                  y="11"
                  width="12"
                  height="10"
                  rx="3"
                  stroke="rgba(15,23,42,0.75)"
                  strokeWidth="2"
                />
              </svg>
              <span>VOCÊ ESTÁ EM AMBIENTE SEGURO</span>
            </div>
          </div>
        </Card>
      </div>
    </form>
  );
}
